
import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import {
    FileText, User, Briefcase, GraduationCap,
    Save, Download, Sparkles, Plus, Trash2,
    ArrowLeft, Palette, Mail, MapPin, Phone,
    Loader2, Image as ImageIcon, Minus,
    Maximize2, Type, Layout, X, Upload,
    Circle, Square, Layers, Copy, MousePointer2,
    AlignLeft, AlignCenter, AlignRight, Bold, Italic, Underline, Strikethrough,
    Move, RotateCw, RotateCcw, ChevronDown, Grid3X3, ArrowUp, ArrowDown, Globe, Triangle, Hexagon,
    Percent, GripVertical, Monitor, Smartphone, Bot, Send, Hand, ALargeSmall, Baseline,
    ArrowRight, ArrowLeft as ArrowLeftIcon, BoxSelect, Maximize, PaintBucket,
    Unlock, Lock, GripHorizontal, FileType, Code2, Code, Search, List, ListOrdered, ShieldCheck, Award, Smile, Link as LinkIcon,
    Wand2, CheckCircle2, XCircle, Split, Printer, Languages, Heart, Users as UsersIcon, BookOpen, Sliders,
    MoreVertical, Table as TableIcon, Settings, LayoutTemplate, ChevronUp, FilePlus, Scroll, Eye, EyeOff, Check, AlertCircle, Cloud
} from 'lucide-react';
import { CVData, User as AppUser, CVSection, CVDesignConfig, CVTemplate, CVDecoration, UserRole } from '../types';
import { supabase_upload_image, supabase_save_cv_template, supabase_get_cv_templates, supabase_save_cv_version, supabase_get_vacancies, extractErrorMessage } from '../services/supabaseService';
import { enhanceTextWithAI, updateCVTemplateWithAI, generateTailoredCV, translateCVData, AI_PROXY_CHAT_MODEL } from '../services/geminiService';
import CVContentSidebar from './CVContentSidebar';
import { DocumentEditor } from './DocumentEditor';
import { generateHTMLFromCV } from '../utils/cvToHtml';

interface CVBuilderProps {
    user: AppUser | null;
    onBack?: () => void;
    initialData?: CVData | null;
    embedded?: boolean;
    mode?: 'design' | 'candidate';
    initialTemplate?: CVTemplate;
    vacancies?: any[];
    candidates?: any[];
    templates?: any[];
    /** Vanuit «Voorgesteld»: toon knop om opgemaakte CV aan voorstel te koppelen. */
    portalProposalAttachMode?: boolean;
    onAttachPortalProposal?: (snapshot: Record<string, unknown>) => void;
}

const GOOGLE_FONTS = [
    { name: 'Inter', family: 'Inter, sans-serif' },
    { name: 'Roboto', family: 'Roboto, sans-serif' },
    { name: 'Open Sans', family: '"Open Sans", sans-serif' },
    { name: 'Lato', family: 'Lato, sans-serif' },
    { name: 'Montserrat', family: 'Montserrat, sans-serif' },
    { name: 'Playfair Display', family: '"Playfair Display", serif' },
    { name: 'Merriweather', family: 'Merriweather, serif' },
    { name: 'Lora', family: 'Lora, serif' },
    { name: 'Poppins', family: 'Poppins, sans-serif' },
    { name: 'Oswald', family: 'Oswald, sans-serif' },
    { name: 'Raleway', family: 'Raleway, sans-serif' },
    { name: 'DM Sans', family: '"DM Sans", sans-serif' }
];

const AVAILABLE_SHORTCODES = [
    { code: '{{name}}', label: 'Naam' },
    { code: '{{role}}', label: 'Functie' },
    { code: '{{email}}', label: 'Email' },
    { code: '{{phone}}', label: 'Telefoon' },
    { code: '{{location}}', label: 'Locatie' },
    { code: '{{linkedin}}', label: 'LinkedIn URL' },
    { code: '{{website}}', label: 'Website' },
];

const CV_SNIPPETS = [
    { label: '2 Kolommen', html: '<div style="display: flex; gap: 20px;"><div style="flex: 1">Links</div><div style="flex: 1">Rechts</div></div>' },
    { label: 'Vaardigheden Lijst', html: '<ul><li>Skill 1</li><li>Skill 2</li><li>Skill 3</li></ul>' },
    { label: 'Contact Regel', html: '<div style="display: flex; gap: 10px; font-size: 0.9em;"><span>ðŸ“ Locatie</span><span>ðŸ“§ Email</span></div>' }
];

const A4_WIDTH_PX = 794;
const A4_HEIGHT_PX = 1123;

const DEFAULT_DESIGN: CVDesignConfig = {
    fontFamily: 'Inter, sans-serif',
    primaryColor: '#000000',
    textColor: '#1e293b',
    backgroundColor: '#ffffff',
    backgroundOverlayColor: '#ffffff',
    backgroundOpacity: 0.1,
    pagePadding: 50,
    paddingTop: 50,
    paddingBottom: 50,
    paddingLeft: 50,
    paddingRight: 50,
    itemSpacing: 20,
    fontSizeBody: 11,
    fontSizeHeading: 24,
    headerStyle: 'standard',
    bulletColor: '#000000'
};

const EditableText: React.FC<any> = ({ value, onChange, style, className, tagName = 'div', placeholder, isRich = false, autoFocus = false, onFocus }) => {
    const contentRef = useRef<HTMLElement>(null);
    const isFocused = useRef(false);

    useEffect(() => {
        if (contentRef.current && !isFocused.current) {
            if (isRich) {
                if (contentRef.current.innerHTML !== value) contentRef.current.innerHTML = value || '';
            } else {
                if (contentRef.current.innerText !== value) contentRef.current.innerText = value || '';
            }
        }
    }, [value, isRich]);

    useEffect(() => {
        if (autoFocus && contentRef.current) {
            contentRef.current.focus();
        }
    }, [autoFocus]);

    const handleInput = () => { if (contentRef.current) onChange(isRich ? contentRef.current.innerHTML : contentRef.current.innerText); };
    const handleFocus = () => { isFocused.current = true; if (onFocus) onFocus(); };
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        let text = e.clipboardData.getData('text/plain');
        text = text.replace(/\r\n/g, '\n').replace(/(?<!\n)\n(?!\n|\s*[-*•])/g, ' ').replace(/ +/g, ' ').trim();
        if (isRich) {
            const html = text.split('\n\n').map((p: string) => p.trim()).filter(Boolean).map((p: string) => p.replace(/\n/g, '<br/>')).join('<br/><br/>');
            document.execCommand('insertHTML', false, html);
        } else {
            document.execCommand('insertText', false, text);
        }
    };
    const Tag = tagName as React.ElementType;

    return (
        <Tag
            ref={contentRef}
            className={`outline-none focus:ring-2 focus:ring-red-/50 rounded px-1 transition-all empty:before:content-[attr(data-placeholder)] empty:before:text-slate-300 cursor-text ${className}`}
            style={{ minWidth: '20px', display: 'inline-block', ...style }}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            onPaste={handlePaste}
            onFocus={handleFocus}
            onBlur={() => isFocused.current = false}
            data-placeholder={placeholder}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
    );
};


// --- Helper Components Moved Before Main Component ---
const ContentEditorModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    data: CVData;
    onChange: (newData: CVData) => void;
}> = ({ isOpen, onClose, data, onChange }) => {
    if (!isOpen) return null;

    // Local state to manage form quickly without re-rendering the whole builder every keystroke (perf)
    const [localData, setLocalData] = useState<CVData>(data);
    const [mode, setMode] = useState<'form' | 'text'>('form');
    const [textData, setTextData] = useState('');
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    useEffect(() => { setLocalData(data); }, [data]);

    // Generate text representation when switching to text mode
    useEffect(() => {
        if (mode === 'text') {
            const lines = [];
            lines.push(`NAAM: ${localData.personalInfo.name}`);
            lines.push(`TITEL: ${localData.title}`);
            lines.push(`EMAIL: ${localData.personalInfo.email}`);
            lines.push(`TELEFOON: ${localData.personalInfo.phone}`);
            lines.push(`\nPROFIEL:\n${localData.summary}`);
            lines.push(`\nWERKERVARING:`);
            localData.workHistory.forEach(w => {
                lines.push(`â€¢ ${w.period}: ${w.role} bij ${w.company}\n${w.description}`);
            });
            lines.push(`\nOPLEIDING:`);
            localData.education.forEach(e => {
                lines.push(`â€¢ ${e.degree} - ${e.school} (${e.year})`);
            });
            lines.push(`\nVAARDIGHEDEN:\n${localData.skills.join(', ')}`);
            setTextData(lines.join('\n'));
        }
    }, [mode]);

    const handleSave = async () => {
        if (mode === 'text') {
            setIsAnalyzing(true);
            try {
                // Dynamically import to avoid circular dependencies if any
                const { parsePasteToProfile } = await import('../services/geminiService');
                const parsed = await parsePasteToProfile(textData);

                // Merge parsed data with existing ID/CandidateID to avoid losing context
                // parsed returns partial structure. We need to map it carefully.
                // parsePasteToProfile returns: {name, title, email, phone, summary, workHistory, education, skills}

                const merged: CVData = {
                    ...localData,
                    personalInfo: {
                        ...localData.personalInfo,
                        name: parsed.name || localData.personalInfo.name,
                        email: parsed.email || localData.personalInfo.email,
                        phone: parsed.phone || localData.personalInfo.phone,
                    },
                    title: parsed.title || localData.title,
                    summary: parsed.summary || localData.summary,
                    workHistory: parsed.workHistory?.map((w: any) => ({
                        role: w.role || '',
                        company: w.company || '',
                        period: w.period || '', // Changed from year to period
                        description: w.description || ''
                    })) || localData.workHistory,
                    education: parsed.education?.map((e: any) => ({
                        degree: e.degree || '',
                        school: e.school || '',
                        year: e.year || ''
                    })) || localData.education,
                    skills: parsed.skills || localData.skills
                };

                onChange(merged);
                onClose();
            } catch (e) {
                console.error(e);
                alert("Kon tekst niet verwerken. Probeer het opnieuw of gebruik het formulier.");
            } finally {
                setIsAnalyzing(false);
            }
        } else {
            onChange(localData);
            onClose();
        }
    };

    return (
        <div className="fixed inset-0 z-[250] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-slate-50 w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scaleIn">
                <div className="p-4 bg-white border-b border-slate-200 flex justify-between items-center">
                    <div className="flex items-center gap-6">
                        <h2 className="text-lg font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                            <FileText className="text-purple-600" /> Inhoud Bewerken
                        </h2>
                        <div className="flex bg-slate-100 p-1 rounded-lg">
                            <button onClick={() => setMode('form')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${mode === 'form' ? 'bg-white text-purple-600 shadow' : 'text-slate-500 hover:text-slate-700'}`}>Formulier</button>
                            <button onClick={() => setMode('text')} className={`px-3 py-1 text-xs font-bold rounded-md transition-all ${mode === 'text' ? 'bg-white text-purple-600 shadow' : 'text-slate-500 hover:text-slate-700'}`}>Vrije Tekst (AI)</button>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 text-slate-500 font-bold text-xs uppercase hover:bg-slate-100 rounded-lg">Annuleren</button>
                        <button onClick={handleSave} disabled={isAnalyzing} className="px-6 py-2 bg-emerald-600 text-white font-bold text-xs uppercase rounded-lg hover:bg-emerald-700 shadow-lg shadow-emerald-100 disabled:opacity-50 flex items-center gap-2">
                            {isAnalyzing ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Opslaan & Sluiten
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar">
                    {mode === 'text' ? (
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-full flex flex-col">
                            <div className="mb-4 bg-red- border border-red- p-4 rounded-xl flex gap-3">
                                <Bot className="text-red- shrink-0" size={24} />
                                <div>
                                    <h4 className="font-bold text-red- text-sm">Slimme Tekst Editor</h4>
                                    <p className="text-xs text-red- mt-1">
                                        Bewerk je CV hieronder als Ã©Ã©n document. Onze AI zal de structuur automatisch herkennen en opslaan.
                                        Zorg dat je kopjes zoals 'Werkervaring' en 'Opleiding' duidelijk houdt.
                                    </p>
                                </div>
                            </div>
                            <textarea
                                value={textData}
                                onChange={e => setTextData(e.target.value)}
                                className="flex-1 w-full p-6 bg-slate-50 border border-slate-200 rounded-xl font-mono text-sm focus:ring-2 focus:ring-purple-500 outline-none leading-relaxed resize-none"
                                placeholder="Plak hier je CV tekst..."
                            />
                        </div>
                    ) : (
                        <>
                            {/* Personalia */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Persoonsgegevens</h3>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Naam</label>
                                        <input value={localData.personalInfo.name} onChange={e => setLocalData({ ...localData, personalInfo: { ...localData.personalInfo, name: e.target.value } })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded font-bold text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Functie Titel</label>
                                        <input value={localData.title} onChange={e => setLocalData({ ...localData, title: e.target.value })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded font-bold text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Email</label>
                                        <input value={localData.personalInfo.email} onChange={e => setLocalData({ ...localData, personalInfo: { ...localData.personalInfo, email: e.target.value } })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded text-sm" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Telefoon</label>
                                        <input value={localData.personalInfo.phone} onChange={e => setLocalData({ ...localData, personalInfo: { ...localData.personalInfo, phone: e.target.value } })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded text-sm" />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Adres</label>
                                        <input value={localData.personalInfo.address || ''} onChange={e => setLocalData({ ...localData, personalInfo: { ...localData.personalInfo, address: e.target.value } })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded text-sm" placeholder="Straatnaam 1" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Postcode</label>
                                        <input value={localData.personalInfo.zipCode || ''} onChange={e => setLocalData({ ...localData, personalInfo: { ...localData.personalInfo, zipCode: e.target.value } })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded text-sm" placeholder="1234 AB" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Woonplaats</label>
                                        <input value={localData.personalInfo.city || ''} onChange={e => setLocalData({ ...localData, personalInfo: { ...localData.personalInfo, city: e.target.value } })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded text-sm" placeholder="Stad" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Land</label>
                                        <input value={localData.personalInfo.country || ''} onChange={e => setLocalData({ ...localData, personalInfo: { ...localData.personalInfo, country: e.target.value } })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded text-sm" placeholder="Land" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase font-bold text-slate-400">Voorkeurstaal</label>
                                        <select value={localData.personalInfo.preferredLanguage || 'nl'} onChange={e => setLocalData({ ...localData, personalInfo: { ...localData.personalInfo, preferredLanguage: e.target.value } })} className="w-full p-2 bg-slate-50 border border-slate-200 rounded text-sm">
                                            <option value="nl">Nederlands</option>
                                            <option value="en">Engels</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Summary */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Profielschets</h3>
                                <textarea
                                    value={localData.summary}
                                    onChange={e => setLocalData({ ...localData, summary: e.target.value })}
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl min-h-[120px] text-sm leading-relaxed"
                                />
                            </div>

                            {/* Work History */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Werkervaring</h3>
                                    <button onClick={() => setLocalData({ ...localData, workHistory: [...localData.workHistory, { role: '', company: '', period: '', description: '' }] })} className="text-xs font-bold text-purple-600 hover:bg-purple-50 px-2 py-1 rounded">+ Toevoegen</button>
                                </div>
                                <div className="space-y-6">
                                    {localData.workHistory.map((item, idx) => (
                                        <div key={idx} className="relative pl-4 border-l-2 border-purple-100 group">
                                            <button onClick={() => setLocalData({ ...localData, workHistory: localData.workHistory.filter((_, i) => i !== idx) })} className="absolute right-0 top-0 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                                            <div className="grid grid-cols-3 gap-2 mb-2">
                                                <input value={item.role} onChange={e => { const list = [...localData.workHistory]; list[idx].role = e.target.value; setLocalData({ ...localData, workHistory: list }); }} placeholder="Functie" className="font-bold text-sm bg-transparent border-b border-transparent focus:border-purple-300 outline-none" />
                                                <input value={item.company} onChange={e => { const list = [...localData.workHistory]; list[idx].company = e.target.value; setLocalData({ ...localData, workHistory: list }); }} placeholder="Bedrijf" className="text-sm bg-transparent border-b border-transparent focus:border-purple-300 outline-none" />
                                                <input value={item.period} onChange={e => { const list = [...localData.workHistory]; list[idx].period = e.target.value; setLocalData({ ...localData, workHistory: list }); }} placeholder="Periode" className="text-sm bg-transparent border-b border-transparent focus:border-purple-300 outline-none" />
                                            </div>
                                            <textarea
                                                value={item.description}
                                                onChange={e => { const list = [...localData.workHistory]; list[idx].description = e.target.value; setLocalData({ ...localData, workHistory: list }); }}
                                                className="w-full p-2 bg-slate-50 rounded text-xs leading-relaxed min-h-[80px]"
                                                placeholder="Beschrijving..."
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Education */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest">Opleidingen</h3>
                                    <button onClick={() => setLocalData({ ...localData, education: [...localData.education, { degree: '', school: '', year: '' }] })} className="text-xs font-bold text-purple-600 hover:bg-purple-50 px-2 py-1 rounded">+ Toevoegen</button>
                                </div>
                                <div className="space-y-4">
                                    {localData.education.map((item, idx) => (
                                        <div key={idx} className="relative pl-4 border-l-2 border-red- group">
                                            <button onClick={() => setLocalData({ ...localData, education: localData.education.filter((_, i) => i !== idx) })} className="absolute right-0 top-0 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={14} /></button>
                                            <div className="grid gap-1">
                                                <input value={item.degree} onChange={e => { const list = [...localData.education]; list[idx].degree = e.target.value; setLocalData({ ...localData, education: list }); }} placeholder="Opleiding" className="font-bold text-sm bg-transparent border-b border-transparent focus:border-red- outline-none" />
                                                <input value={item.school} onChange={e => { const list = [...localData.education]; list[idx].school = e.target.value; setLocalData({ ...localData, education: list }); }} placeholder="Instituut" className="text-xs text-slate-500 bg-transparent border-b border-transparent focus:border-red- outline-none" />
                                                <input value={item.year} onChange={e => { const list = [...localData.education]; list[idx].year = e.target.value; setLocalData({ ...localData, education: list }); }} placeholder="Jaar" className="text-xs text-slate-400 bg-transparent border-b border-transparent focus:border-red- outline-none" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Skills */}
                            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                                <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-4">Vaardigheden (Comma separated)</h3>
                                <textarea
                                    value={localData.skills.join(', ')}
                                    onChange={e => setLocalData({ ...localData, skills: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                                    className="w-full p-4 bg-slate-50 border border-slate-200 rounded-xl min-h-[80px] text-sm leading-relaxed"
                                    placeholder="Java, React, Sales..."
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// Helper component to list versions
const ExistingVersionsList: React.FC<{ candidateId: string, onSelect: (name: string) => void }> = ({ candidateId, onSelect }) => {
    const [versions, setVersions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            if (!candidateId) return;
            // Dynamic import to avoid circular dependency issues if any, or just cleanliness
            const { supabase } = await import('../services/supabaseService');
            const { data } = await supabase.from('applications').select('id, cv_summary, updated_at').eq('id', candidateId).order('updated_at', { ascending: false });
            setVersions(data || []);
            setLoading(false);
        };
        fetch();
    }, [candidateId]);

    if (loading) return <div className="p-4 text-center text-slate-400 text-xs">Laden...</div>;
    if (versions.length === 0) return <div className="p-4 text-center text-slate-400 text-xs">Nog geen opgeslagen versies.</div>;

    return (
        <div className="space-y-1">
            {versions.map(v => (
                <button
                    key={v.id}
                    onClick={() => onSelect(v.cv_summary)}
                    className="w-full text-left p-2 hover:bg-white rounded flex items-center justify-between group transition-colors"
                >
                    <span className="text-xs font-medium text-slate-700">{v.cv_summary}</span>
                    <span className="text-[10px] text-slate-400">{new Date(v.updated_at).toLocaleDateString()}</span>
                </button>
            ))}
        </div>
    );
};

// --- Template Processor ---
// --- Template Processor ---
const processTemplate = (template: string, data: CVData): string => {
    // DEBUG: Log data to see what we're working with
    console.log('processTemplate data:', data);

    if (!template) return '';
    let result = template;

    // Helper to get case-insensitive property
    const getProp = (obj: any, keys: string[]) => {
        if (!obj) return '';
        for (const key of keys) {
            if (obj[key] !== undefined && obj[key] !== null) return obj[key];
            if (obj[key.toLowerCase()] !== undefined && obj[key.toLowerCase()] !== null) return obj[key.toLowerCase()];
        }
        return '';
    };

    // 1. Handle Loops: {#collection} ... {/collection}
    const loopRegex = /\{#(\w+)\}([\s\S]*?)\{\/\1\}/g;
    result = result.replace(loopRegex, (match, collectionName, content) => {
        let items: any[] = [];
        const lowerName = collectionName.toLowerCase();

        if (lowerName === 'werkervaring' || lowerName === 'workhistory') items = data.workHistory || [];
        else if (lowerName === 'opleiding' || lowerName === 'education') items = data.education || [];
        else if (lowerName === 'vaardigheden' || lowerName === 'skills') items = data.skills || [];
        else items = (data as any)[collectionName] || (data as any)[lowerName] || [];

        if (!Array.isArray(items) || items.length === 0) return '';

        return items.map((item: any) => {
            let itemContent = content;
            // Replace variables within the loop item
            itemContent = itemContent.replace(/\{(\w+)\}/g, (m: string, key: string) => {
                const lowerKey = key.toLowerCase();
                // Specific item mapping
                if (lowerKey === 'role' || lowerKey === 'functie' || lowerKey === 'titel') return getProp(item, ['role', 'title', 'name']);
                if (lowerKey === 'company' || lowerKey === 'bedrijf' || lowerKey === 'organisatie') return getProp(item, ['company', 'school', 'institution']);
                if (lowerKey === 'period' || lowerKey === 'periode' || lowerKey === 'datum' || lowerKey === 'year' || lowerKey === 'jaar') return getProp(item, ['period', 'year', 'date']);
                if (lowerKey === 'description' || lowerKey === 'omschrijving') return getProp(item, ['description', 'summary']);
                if (lowerKey === 'degree' || lowerKey === 'diploma' || lowerKey === 'studie') return getProp(item, ['degree', 'study', 'name']);

                // Fallback to direct property access
                const val = item[key] || item[lowerKey];
                return val !== undefined && val !== null ? String(val) : '';
            });
            return itemContent;
        }).join('');
    });

    // 2. Handle Global Variables (outside loops or remaining)
    result = result.replace(/\{(\w+(\.\w+)?)\}/g, (match, key) => {
        const lowerKey = key.toLowerCase();

        // Personal Info Mapping
        if (lowerKey === 'name' || lowerKey === 'naam') return getProp(data.personalInfo, ['name', 'fullName']);
        if (lowerKey === 'email') return getProp(data.personalInfo, ['email']);
        if (lowerKey === 'phone' || lowerKey === 'telefoon') return getProp(data.personalInfo, ['phone']);
        if (lowerKey === 'location' || lowerKey === 'locatie' || lowerKey === 'city' || lowerKey === 'stad') return getProp(data.personalInfo, ['location', 'city']);
        if (lowerKey === 'linkedin') return getProp(data.personalInfo, ['linkedin']);
        if (lowerKey === 'dateofbirth' || lowerKey === 'geboortedatum') return getProp(data.personalInfo, ['dateOfBirth']);
        if (lowerKey === 'placeofbirth' || lowerKey === 'geboorteplaats') return getProp(data.personalInfo, ['placeOfBirth']);
        if (lowerKey === 'website') return getProp(data.personalInfo, ['website']);

        // Top Level Mapping
        if (lowerKey === 'summary' || lowerKey === 'profiel' || lowerKey === 'profile') return data.summary || '';
        if (lowerKey === 'title' || lowerKey === 'role' || lowerKey === 'functie') return data.title || '';

        // Check deep path if needed (e.g. personalInfo.name)
        if (key.includes('.')) {
            const parts = key.split('.');
            let val: any = data;
            for (const p of parts) {
                if (val === undefined || val === null) break;
                val = val[p];
            }
            return val !== undefined && val !== null ? String(val) : '';
        }

        return match;
    });

    return result;
};

const CVBuilder: React.FC<CVBuilderProps> = ({ user, onBack, initialData, embedded = false, mode = 'candidate', initialTemplate, candidates, vacancies: vacanciesProp, templates, portalProposalAttachMode, onAttachPortalProposal }) => {

    const [activeToolTab, setActiveToolTab] = useState<'design' | 'elements' | 'structure' | 'uploads' | 'ai_chat' | 'cv_tailor' | 'shortcodes' | 'edit_element' | 'edit_section' | 'translate'>('design');
    const [activeToolMode, setActiveToolMode] = useState<'select' | 'pan'>('select');
    const [isFullscreen, setIsFullscreen] = useState(false);

    // View Mode: visual (paged), text (editor), continuous, export, collabora
    const [viewMode, setViewMode] = useState<'continuous' | 'paged' | 'export' | 'collabora'>('continuous'); // Default to continuous for "One Document" feel
    const [isPreviewMode, setIsPreviewMode] = useState(false); // Toggle between editing tokens and seeing processed result
    
    // Pro Template (Word)     // Export view state
    const [selectedProTemplateId, setSelectedProTemplateId] = useState<string | null>(null);
    const [isGeneratingWord, setIsGeneratingWord] = useState(false);
    const [exportSearchTerm, setExportSearchTerm] = useState('');
    const [exportTab, setExportTab] = useState<'templates' | 'shortcodes'>('templates');

    const [isTranslating, setIsTranslating] = useState(false);
    
    const handleTranslateCV = async (targetLang: string) => {
        setIsTranslating(true);
        try {
            const { translateFullCv } = await import('../services/geminiService');
            const translatedData = await translateFullCv(resumeData, targetLang);
            if(translatedData && typeof translatedData === 'object' && !translatedData.error) {
                setResumeData({ ...translatedData, language: targetLang });
                
                // Translate section titles in the layout pages
                setPages(prev => {
                    const dict: Record<string, any> = {
                        en: {
                            'Profiel': 'Profile', 'Profielschets': 'Profile',
                            'Werkervaring': 'Work Experience', 'Ervaring': 'Experience',
                            'Opleidingen': 'Education', 'Opleiding': 'Education',
                            'Vaardigheden': 'Skills', 'Talen': 'Languages',
                            'Hobby\'s': 'Hobbies', 'Interesses': 'Interests',
                            'Referenties': 'References', 'Cursussen': 'Courses', 'Certificaten': 'Certificates'
                        },
                        nl: {
                            'Profile': 'Profiel',
                            'Work Experience': 'Werkervaring', 'Experience': 'Ervaring',
                            'Education': 'Opleidingen',
                            'Skills': 'Vaardigheden', 'Languages': 'Talen',
                            'Hobbies': 'Hobby\'s', 'Interests': 'Interesses',
                            'References': 'Referenties', 'Courses': 'Cursussen', 'Certificates': 'Certificaten'
                        }
                    };
                    const langDict = dict[targetLang] || {};

                    const translateStructure = (structure: CVSection[][]): CVSection[][] => {
                        const mapSection = (sec: CVSection): CVSection => {
                            let newTitle = sec.title;
                            if (newTitle && langDict[newTitle]) {
                                newTitle = langDict[newTitle];
                            }
                            let newColumns = sec.columns;
                            if (sec.columns) {
                                newColumns = sec.columns.map(col => mapSection(col));
                            }
                            return { ...sec, title: newTitle, columns: newColumns };
                        };
                        return structure.map(page => page.map(sec => mapSection(sec)));
                    };

                    const structureWithTranslatedTitles = translateStructure(prev);
                    return mergeDataIntoStructure(structureWithTranslatedTitles, translatedData);
                });

                recordHistory();
                setActiveToolTab('design'); 
            } else {
                alert("Fout bij vertalen: ongeldig formaat.");
            }
        } catch(e: any) {
            console.error("Vertaalfout:", e);
            alert("Vertalen mislukt. " + e.message);
        } finally {
            setIsTranslating(false);
        }
    };

    // Initial State Setup 
    const [design, setDesign] = useState<CVDesignConfig>(() => {
        const templateDesign = initialTemplate?.design || DEFAULT_DESIGN;
        return {
            ...DEFAULT_DESIGN,
            ...templateDesign,
            pagePadding: Number((templateDesign as any).pagePadding || DEFAULT_DESIGN.pagePadding),
            paddingTop: Number((templateDesign as any).paddingTop ?? ((templateDesign as any).pagePadding || DEFAULT_DESIGN.pagePadding)),
            paddingBottom: Number((templateDesign as any).paddingBottom ?? ((templateDesign as any).pagePadding || DEFAULT_DESIGN.pagePadding)),
            paddingLeft: Number((templateDesign as any).paddingLeft ?? ((templateDesign as any).pagePadding || DEFAULT_DESIGN.pagePadding)),
            paddingRight: Number((templateDesign as any).paddingRight ?? ((templateDesign as any).pagePadding || DEFAULT_DESIGN.pagePadding)),
            itemSpacing: Number((templateDesign as any).itemSpacing || DEFAULT_DESIGN.itemSpacing),
            fontSizeBody: Number((templateDesign as any).fontSizeBody || DEFAULT_DESIGN.fontSizeBody),
            fontSizeHeading: Number((templateDesign as any).fontSizeHeading || DEFAULT_DESIGN.fontSizeHeading),
        };
    });

    const [resumeData, setResumeData] = useState<CVData>(initialData || {
        title: '', summary: '', personalInfo: { name: '', email: '', phone: '', location: '', linkedin: '' },
        workHistory: [], education: [], skills: []
    });




    // Helper function to merge data into ANY structure

    const mergeDataIntoStructure = (structure: CVSection[][], data: CVData): CVSection[][] => {
        const mapSection = (sec: CVSection): CVSection => {
            // Deep clone content
            let newContent: any = sec.content ? { ...sec.content } : {};

            if (sec.type === 'header') {
                if (data.personalInfo.name) newContent.name = data.personalInfo.name;
                if (data.title) newContent.role = data.title;
                if (data.personalInfo.email) newContent.email = data.personalInfo.email;
                if (data.personalInfo.phone) newContent.phone = data.personalInfo.phone;
            } else if (sec.type === 'summary') {
                if (data.summary) newContent.text = data.summary;
            } else if (sec.type === 'experience' && data.workHistory?.length > 0) {
                newContent.items = data.workHistory;
            } else if (sec.type === 'education' && data.education?.length > 0) {
                newContent.items = data.education;
            } else if (sec.type === 'skills' && data.skills?.length > 0) {
                newContent.items = data.skills;
            }

            let newColumns = sec.columns;
            if (sec.columns) {
                newColumns = sec.columns.map(col => mapSection(col));
            }

            return { ...sec, content: newContent, columns: newColumns };
        };

        if (structure.length > 0) {
            return structure.map((page: CVSection[]) =>
                page.map((sec: CVSection) => mapSection(sec))
            );
        }
        return structure;
    };

    const [pages, setPages] = useState<CVSection[][]>(() => {
        let loadedStructure = initialTemplate?.structure ? JSON.parse(JSON.stringify(initialTemplate.structure)) : [];

        if (initialData) {
            if (loadedStructure.length > 0) {
                loadedStructure = mergeDataIntoStructure(loadedStructure, initialData);
            } else {
                loadedStructure = [[
                    { id: 'header', type: 'header', width: 100, content: { name: initialData.personalInfo.name || '{{name}}', role: initialData.title || '{{role}}' }, settings: { showEmail: true, showPhone: true, reverseOrder: false } },
                    { id: 'summary', type: 'summary', title: 'Profiel', width: 100, content: { text: initialData.summary || 'Profielschets...' } },
                    { id: 'experience', type: 'experience', title: 'Werkervaring', width: 100, content: { items: initialData.workHistory || [] } },
                ]];
            }
        } else if (loadedStructure.length === 0) {
            loadedStructure = [[
                { id: 'header', type: 'header', width: 100, content: { name: 'Naam', role: 'Functie' }, settings: { showEmail: true, showPhone: true } },
                { id: 'summary', type: 'summary', title: 'Profiel', width: 100, content: { text: 'Profielschets...' } }
            ]];
        }

        return loadedStructure;
    });

    // --- View Mode Logic ---
    const [editorText, setEditorText] = useState<string>('');
    const [isApplyingText, setIsApplyingText] = useState<boolean>(false);

    // --- View Mode Logic Removed (Reverted to Visual Only) ---


    const [decorations, setDecorations] = useState<CVDecoration[]>(() => {
        let decos = initialTemplate?.decorations ? JSON.parse(JSON.stringify(initialTemplate.decorations)) : [];
        if (initialData && (initialData.personalInfo as any).photoUrl) {
            const photoUrl = (initialData.personalInfo as any).photoUrl;
            if (!decos.some((d: any) => d.type === 'image' && d.imageUrl === photoUrl)) {
                decos.push({
                    id: 'auto-profile-photo',
                    type: 'image',
                    x: 650, y: 50, width: 100, height: 100,
                    opacity: 1, zIndex: 50, pageIndex: 0, rotation: 0,
                    imageUrl: photoUrl,
                    allPages: false
                });
            }
        }
        return decos;
    });

    const [selectedDecoId, setSelectedDecoId] = useState<string | null>(null);
    const [past, setPast] = useState<any[]>([]);
    const [future, setFuture] = useState<any[]>([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [isUploadingToolbar, setIsUploadingToolbar] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const sidebarFileRef = useRef<HTMLInputElement>(null);
    const importTemplateInputRef = useRef<HTMLInputElement>(null);

    // --- SPLIT SCREEN & AI TAILORING STATE ---
    const [isTailoring, setIsTailoring] = useState(false);
    const [tailorResult, setTailorResult] = useState<any>(null);
    const [selectedVacancyId, setSelectedVacancyId] = useState<string>('');
    const [localVacancies, setLocalVacancies] = useState<any[]>([]);
    const vacancies = vacanciesProp || localVacancies;
    const [isAiProcessing, setIsAiProcessing] = useState(false);
    const [showTailorAll, setShowTailorAll] = useState(false); // Can be boolean or a specific section ID
    const [expandedTailorSections, setExpandedTailorSections] = useState<string[]>([]);
    const [customAiPrompt, setCustomAiPrompt] = useState('');
    const [showOriginalFor, setShowOriginalFor] = useState<Record<number, boolean>>({});
    const [isTemplateMode, setIsTemplateMode] = useState(false); // New State for Template Mode

    // NEW STATE FOR CANDIDATE SYNC IN CV BUILDER
    const [selectedCandidateId, setSelectedCandidateId] = useState<string>(initialData?.candidateId || initialData?.id || '');

    const [draggedSection, setDraggedSection] = useState<{ pageIndex: number, sectionIndex: number } | null>(null);
    const repaginateTimeoutRef = useRef<any>(null);

    // Save Warning State
    const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);
    const [pendingSaveName, setPendingSaveName] = useState('');

    const recordHistory = useCallback(() => {
        setPast(currentPast => {
            const newHistory = [...currentPast, {
                design: JSON.parse(JSON.stringify(design)),
                pages: JSON.parse(JSON.stringify(pages)),
                decorations: JSON.parse(JSON.stringify(decorations))
            }];
            return newHistory.length > 20 ? newHistory.slice(newHistory.length - 20) : newHistory;
        });
        setFuture([]);
    }, [design, pages, decorations]);

    const handleUndo = useCallback(() => {
        if (past.length === 0) return;
        const previous = past[past.length - 1];
        const newPast = past.slice(0, past.length - 1);
        setFuture(currentFuture => [{ design: JSON.parse(JSON.stringify(design)), pages: JSON.parse(JSON.stringify(pages)), decorations: JSON.parse(JSON.stringify(decorations)) }, ...currentFuture]);
        setDesign(previous.design);
        setPages(previous.pages);
        setDecorations(previous.decorations);
        setPast(newPast);
    }, [past, design, pages, decorations]);

    const handleRedo = useCallback(() => {
        if (future.length === 0) return;
        const next = future[0];
        const newFuture = future.slice(1);
        setPast(currentPast => [...currentPast, { design: JSON.parse(JSON.stringify(design)), pages: JSON.parse(JSON.stringify(pages)), decorations: JSON.parse(JSON.stringify(decorations)) }]);
        setDesign(next.design);
        setPages(next.pages);
        setDecorations(next.decorations);
        setFuture(newFuture);
    }, [future, design, pages, decorations]);

    const handleTemplateImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setIsAiProcessing(true);
        console.log("Analyzing file via AI (Azure proxy)...", file.name);

        try {
            // 1. Convert file to Base64
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = async () => {
                const base64 = (reader.result as string).split(',')[1];
                const mimeType = file.type;

                const apiBase =
                    (import.meta as any).env?.VITE_API_URL ||
                    (typeof window !== 'undefined' && window.location.hostname !== 'localhost'
                        ? ''
                        : 'http://localhost:4000');

                const promptText = `
                Analyze this CV document/image. Extract the design, layout, and structure.
                
                Critically analyze the SECTION TITLES placement. 
                - If the section titles (e.g. "Experience", "Education") are ABOVE the content, set "sectionTitleLayout": "top".
                - If the section titles are in a LEFT COLUMN/SIDEBAR and the content is on the right (Side-by-Side layout), set "sectionTitleLayout": "left".
                
                Return ONLY a valid JSON object with this exact structure:
                {
                    "design": {
                        "primaryColor": "hex", 
                        "secondaryColor": "hex", 
                        "textColor": "hex", 
                        "backgroundColor": "hex",
                        "fontFamily": "standard web font name", 
                        "headerStyle": "standard" | "sidebar" | "banner",
                        "sectionTitleLayout": "top" | "left",
                        "bulletColor": "hex",
                        "tagColor": "hex",
                        "layoutVariant": "classic" | "modern" | "compact"
                    },
                    "layout": {
                        "type": "single_column" | "sidebar_left" | "sidebar_right",
                        "sidebarWidth": number (percentage 0-100, default 30)
                    },
                    "sections": [
                        { "type": "header" | "summary" | "experience" | "education" | "skills" | "custom_text", "placement": "main" | "sidebar" }
                    ],
                    "decorations": [
                         { "type": "circle" | "square" | "line", "x": number (px), "y": number (px), "width": number (px), "height": number (px), "color": "hex", "opacity": number (0-1), "pageIndex": 0 }
                    ]
                }
                If uncertain, suggest a professional matching style.
                `;

                const response = await fetch(`${apiBase.replace(/\/$/, '')}/api/gemini/generate`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        model: AI_PROXY_CHAT_MODEL,
                        contents: [
                            {
                                role: 'user',
                                parts: [
                                    { text: promptText },
                                    { inlineData: { mimeType, data: base64 } },
                                ],
                            },
                        ],
                        config: { responseMimeType: 'application/json' },
                    }),
                });

                if (!response.ok) {
                    const errText = await response.text();
                    throw new Error(`AI Error: ${response.status} ${response.statusText} - ${errText}`);
                }

                const data = await response.json();
                const text = data.text;

                if (text) {
                    try {
                        const extracted = JSON.parse(text);
                        console.log("AI Extracted Full Design:", extracted);

                        recordHistory();

                        // 1. Apply Design
                        setDesign(prev => ({
                            ...prev,
                            primaryColor: extracted.design?.primaryColor || prev.primaryColor,
                            secondaryColor: extracted.design?.secondaryColor || prev.secondaryColor,
                            textColor: extracted.design?.textColor || prev.textColor,
                            fontFamily: extracted.design?.fontFamily || prev.fontFamily,
                            headerStyle: extracted.design?.headerStyle || prev.headerStyle,
                            bulletColor: extracted.design?.bulletColor || extracted.design?.primaryColor || prev.bulletColor,
                        }));

                        // 2. Apply Layout & Sections
                        if (extracted.sections && Array.isArray(extracted.sections)) {
                            const newPages: CVSection[][] = [[]]; // Start with empty page 1

                            // Helper to create section
                            const createSection = (type: string): CVSection => {
                                const newSec: CVSection = {
                                    id: Math.random().toString(),
                                    type: type as any,
                                    width: 100,
                                    content: {}
                                };
                                // Defaults (mirrors handleAddSection)
                                if (type === 'header') { newSec.content = { name: 'Naam', role: 'Functie', email: 'email@example.com', phone: '06-12345678' }; newSec.settings = { showEmail: true, showPhone: true }; }
                                if (type === 'summary') { newSec.title = 'Profielschets'; newSec.content = { text: 'Plaats hier een korte samenvatting...' }; }
                                if (type === 'experience') { newSec.title = 'Werkervaring'; newSec.content = { items: [{ role: 'Functie', company: 'Bedrijf', period: '2020 - Heden', description: 'Beschrijving...' }] }; }
                                if (type === 'education') { newSec.title = 'Opleiding'; newSec.content = { items: [{ school: 'School', degree: 'Opleiding', year: '2019' }] }; }
                                if (type === 'skills') { newSec.title = 'Vaardigheden'; newSec.content = { items: ['Skill 1', 'Skill 2'] }; }
                                if (type === 'custom_text') { newSec.content = { text: 'Vrije tekst...' }; }
                                return newSec;
                            };

                            // Distribute Sections
                            extracted.sections.forEach((sec: any) => {
                                const cvSec = createSection(sec.type);
                                // If sidebar layout, we might want to adjust width or wrap in columns?
                                // For simplicity in this version, we just append them linearly.
                                // Real column support requires a 'columns' section type or setting width < 100.

                                if (extracted.layout?.type?.includes('sidebar') && sec.placement === 'sidebar') {
                                    cvSec.width = extracted.layout.sidebarWidth || 30;
                                    // TODO: Sidebar logic is complex (absolute positioning or flex). 
                                    // For now, we just set width. 
                                } else if (extracted.layout?.type?.includes('sidebar') && sec.placement === 'main') {
                                    cvSec.width = 100 - (extracted.layout.sidebarWidth || 30);
                                    // This assumes they are side-by-side which requires a parent container.
                                    // Current CVBuilder is linear flow.
                                    // We will just append for now.
                                }

                                newPages[0].push(cvSec);
                            });

                            if (newPages[0].length > 0) {
                                setPages(newPages);
                                setTimeout(repaginate, 200); // Trigger repagination to flow content
                            }
                        }

                        // 3. Apply Decorations
                        if (extracted.decorations && Array.isArray(extracted.decorations)) {
                            const newDecos = extracted.decorations.map((d: any) => ({
                                id: Math.random().toString(),
                                type: d.type,
                                x: d.x, y: d.y, width: d.width, height: d.height,
                                opacity: d.opacity ?? 1,
                                color: d.color,
                                zIndex: 10,
                                pageIndex: d.pageIndex || 0,
                                rotation: 0
                            }));
                            setDecorations(prev => [...prev, ...newDecos]);
                        }

                        alert("Full CV Design & Layout Imported!");
                    } catch (e) {
                        console.error("Failed to parse AI response:", text, e);
                        alert("AI Analysis style extraction failed.");
                    }
                } else {
                    alert("AI Analysis returned no results.");
                }

                setIsAiProcessing(false);
                if (e.target) e.target.value = '';
            };
            reader.onerror = (err) => {
                console.error("File Read Error:", err);
                setIsAiProcessing(false);
            };
        } catch (error) {
            console.error("AI Import Error:", error);
            alert(`Failed to analyze document: ${error instanceof Error ? error.message : 'Unknown error'}`);
            setIsAiProcessing(false);
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); }
            if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) { e.preventDefault(); handleRedo(); }
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedDecoId) {
                recordHistory();
                setDecorations(prev => prev.filter(d => d.id !== selectedDecoId));
                setSelectedDecoId(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo, selectedDecoId]);

    // Template type is handled on save
    // PDF Preview Modal State

    // PDF Preview Modal State
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
    const [pdfPreviewBlob, setPdfPreviewBlob] = useState<Blob | null>(null);
    const [isConvertingWord, setIsConvertingWord] = useState(false);
    const [templateName, setTemplateName] = useState(initialTemplate?.name || (initialData ? 'CV Versie' : 'Nieuw Template'));
    const [currentTemplateId, setCurrentTemplateId] = useState<string>(initialTemplate?.id || 'default');
    const [availableTemplates, setAvailableTemplates] = useState<CVTemplate[]>([]);
    const [selectedSection, setSelectedSection] = useState<{ pageIndex: number; sectionId: string } | null>(null);

    const [zoomLevel, setZoomLevel] = useState(0.8);
    const [dragState, setDragState] = useState<{
        isDragging: boolean; isResizing: boolean; type: 'decoration' | 'section'; targetId: string; pageIndex?: number; handle?: string; startX: number; startY: number; initialX: number; initialY: number; initialW: number; initialH: number;
    } | null>(null);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!dragState) return;
            const { type, targetId, startX, startY, initialX, initialY, initialW, initialH, pageIndex, handle } = dragState;
            const deltaX = (e.clientX - startX) / zoomLevel;
            const deltaY = (e.clientY - startY) / zoomLevel;

            if (dragState.isDragging) {
                if (type === 'decoration') {
                    setDecorations(prev => prev.map(d => d.id === targetId ? { ...d, x: initialX + deltaX, y: initialY + deltaY } : d));
                } else if (type === 'section' && typeof pageIndex === 'number') {
                    setPages(prev => {
                        const newPages = [...prev];
                        const section = newPages[pageIndex].find(s => s.id === targetId);
                        if (section && section.styles?.position === 'absolute') {
                            section.styles.left = `${initialX + deltaX}px`;
                            section.styles.top = `${initialY + deltaY}px`;
                        }
                        return newPages;
                    });
                }
            } else if (dragState.isResizing) {
                if (type === 'decoration') {
                    if (handle === 'se') setDecorations(prev => prev.map(d => d.id === targetId ? { ...d, width: Math.max(10, initialW + deltaX), height: Math.max(10, initialH + deltaY) } : d));
                    else if (handle === 'sw') setDecorations(prev => prev.map(d => d.id === targetId ? { ...d, x: initialX + deltaX, width: Math.max(10, initialW - deltaX), height: Math.max(10, initialH + deltaY) } : d));
                    else if (handle === 'ne') setDecorations(prev => prev.map(d => d.id === targetId ? { ...d, y: initialY + deltaY, width: Math.max(10, initialW + deltaX), height: Math.max(10, initialH - deltaY) } : d));
                    else if (handle === 'nw') setDecorations(prev => prev.map(d => d.id === targetId ? { ...d, x: initialX + deltaX, y: initialY + deltaY, width: Math.max(10, initialW - deltaX), height: Math.max(10, initialH - deltaY) } : d));
                } else if (type === 'section' && typeof pageIndex === 'number') {
                    const percentChange = (deltaX / A4_WIDTH_PX) * 100;
                    const newWidth = Math.min(100, Math.max(20, (initialW || 100) + percentChange));
                    const newPages = [...pages];
                    const section = newPages[pageIndex].find(s => s.id === targetId);
                    if (section) { section.width = newWidth; setPages(newPages); }
                }
            }
        };
        const handleMouseUp = () => { if (dragState) { recordHistory(); setDragState(null); } };
        if (dragState) { window.addEventListener('mousemove', handleMouseMove); window.addEventListener('mouseup', handleMouseUp); }
        return () => { window.removeEventListener('mousemove', handleMouseMove); window.removeEventListener('mouseup', handleMouseUp); };
    }, [dragState, zoomLevel, pages, recordHistory]);

    const [savingTemplate, setSavingTemplate] = useState(false);
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showSaveVersionModal, setShowSaveVersionModal] = useState(false);
    const [showContentEditor, setShowContentEditor] = useState(false); // Kept for legacy/modal access if needed
    const [savedVersionName, setSavedVersionName] = useState('');
    const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'ai', text: string }[]>([{ role: 'ai', text: 'Hoi! Ik ben Froukje. Ik kan het design aanpassen.' }]);
    const [chatInput, setChatInput] = useState('');
    const [isChatProcessing, setIsChatProcessing] = useState(false);
    const chatEndRef = useRef<HTMLDivElement>(null);
    const [documentHtml, setDocumentHtml] = useState<string>('');

    // Initialize Document Editor content when switching to continuous mode for the first time
    useEffect(() => {
        if (viewMode === 'continuous' && !documentHtml && resumeData) {
            setDocumentHtml(generateHTMLFromCV(resumeData, design));
        }
    }, [viewMode, resumeData, design, documentHtml]);

    useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);
    useEffect(() => {
        if (selectedDecoId) setActiveToolTab('edit_element');
        else if (selectedSection) setActiveToolTab('edit_section');
        else if (activeToolTab === 'edit_element' || activeToolTab === 'edit_section') setActiveToolTab('design');
    }, [selectedDecoId, selectedSection]);

    useEffect(() => {
        const fetchTemplates = async () => {
            const filterId = (user?.role === UserRole.CLIENT || user?.role === UserRole.CLIENT_RECRUITER) ? user.clientId : undefined;
            const temps = await supabase_get_cv_templates(filterId);
            let finalTemps = temps || [];
            if (initialTemplate && initialTemplate.id !== 'default' && !finalTemps.find(t => t.id === initialTemplate.id)) {
                finalTemps = [initialTemplate, ...finalTemps];
            }
            setAvailableTemplates(finalTemps);

            // Auto-select first template and open Inhoud Editor if coming straight from Talentpool Candidate View
            if (initialData && !initialTemplate && finalTemps.length > 0) {
                handleLoadTemplate(finalTemps[0]);
                setShowTemplates(false);
                setTimeout(() => setShowContentEditor(true), 300); // Small delay to ensure render loop settles
            }
        };
        fetchTemplates();
        const fetchVacancies = async () => {
            if (vacanciesProp) return; // Skip fetch if passed as prop
            const filterId = (user?.role === UserRole.CLIENT || user?.role === UserRole.CLIENT_RECRUITER) ? user.clientId : undefined;
            const data = await supabase_get_vacancies(filterId);
            if (data) {
                const uniqueVac = Array.from(new Map(data.map((v: any) => [v.id, v])).values());
                setLocalVacancies(uniqueVac);
            }
        };
        fetchVacancies();
    }, [initialTemplate, user, vacanciesProp]);

    // Update form data when a new candidate is selected from the combobox
    useEffect(() => {
        if (!selectedCandidateId || !candidates) return;
        if (selectedCandidateId === (resumeData as any)?.candidateId || selectedCandidateId === resumeData?.id) return;
        
        const cand = candidates.find(c => c.candidate?.id === selectedCandidateId || c.id === selectedCandidateId);
        if (cand) {
            const parsed = cand.parsed_cv || cand.ai_parsed_data || {};
            const basic = cand.candidate || cand;
            const newData = {
                id: cand.id,
                candidateId: basic.id,
                title: basic.role || '',
                summary: parsed.summary || parsed.profiel || '',
                personalInfo: {
                    name: basic.name || '',
                    email: basic.email || '',
                    phone: basic.phone || '',
                    location: basic.city || basic.location || '',
                    linkedin: parsed.linkedin || ''
                },
                workHistory: parsed.workHistory || parsed.werkervaring || [],
                education: parsed.education || parsed.opleiding || [],
                skills: parsed.skills || parsed.vaardigheden || [],
                customFields: parsed.customFields || []
            };

            setResumeData(newData);
            setDocumentHtml(''); // Clear raw HTML to force rebuild
            
            // Sync new data into visual blocks
            setPages(prev => mergeDataIntoStructure(prev, newData));
        }
    }, [selectedCandidateId, candidates]);
    useLayoutEffect(() => {
        const calculateZoom = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.clientWidth;
                const horizontalPadding = 64;
                const availableWidth = Math.max(100, containerWidth - horizontalPadding);
                const fitWidthScale = availableWidth / A4_WIDTH_PX;
                let optimalScale = Math.min(fitWidthScale, 1.2);
                optimalScale = Math.max(0.3, optimalScale);
                setZoomLevel(optimalScale);
            }
        };
        calculateZoom();
        const observer = new ResizeObserver(calculateZoom);
        if (containerRef.current) observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [embedded, isTailoring]);

    useEffect(() => {
        const linkId = 'cv-builder-fonts';
        if (!document.getElementById(linkId)) {
            const link = document.createElement('link');
            link.id = linkId;
            link.href = `https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Inter:wght@300;400;600;700&family=Lato:wght@300;400;700&family=Lora:ital,wght@0,400;0,600;1,400&family=Merriweather:wght@300;400;700&family=Montserrat:wght@300;400;600;700&family=Open+Sans:wght@300;400;600;700&family=Oswald:wght@300;400;500;700&family=Playfair+Display:wght@400;600;700&family=Poppins:wght@300;400;500;600;700&family=Raleway:wght@300;400;600;700&family=Roboto:wght@300;400;500;700&display=swap`;
            link.rel = 'stylesheet';
            document.head.appendChild(link);
        }
    }, []);



    useEffect(() => {
        if (repaginateTimeoutRef.current) clearTimeout(repaginateTimeoutRef.current);
        repaginateTimeoutRef.current = setTimeout(repaginate, 500);
        return () => { if (repaginateTimeoutRef.current) clearTimeout(repaginateTimeoutRef.current); };
    }, [pages, design, viewMode, resumeData]); // Added resumeData dependency

    const estimateItemHeight = (item: any, type: string) => {
        if (type === 'education') return 80;
        if (type === 'experience') {
            const base = 60;
            const textLen = item.description?.length || 0;
            return base + (textLen / 3.5); 
            // A line is ~20px high. ~80 chars per line? => 0.25 px/char? 
            // Let's be conservative: 1 line = 60 chars. 20px / 60 = 0.33. 
            // So / 3 is safe.
        }
        if (type === 'custom_text') {
            const base = 40;
            const textLen = (item.value?.length || item.text?.length || 0);
            return base + (textLen / 3.5);
        }
        return 50;
    };

    // --- SMART REPAGINATION LOGIC ---
    const repaginate = () => {
        const allSections = pages.flat();
        const startIds = allSections.map(s => s.id).join(',');

        const newPages: CVSection[][] = [[]];
        let currentPageIndex = 0;
        let currentHeight = 0;

        const verticalPadding = (design.paddingTop ?? design.pagePadding) + (design.paddingBottom ?? design.pagePadding);
        const SAFE_HEIGHT = A4_HEIGHT_PX - verticalPadding - 40; // Buffer

        const processedTypes = new Set<string>();

        for (const section of allSections) {
            // Logic for List Sections (Experience, Education, Custom Fields)
            if (['experience', 'education', 'custom_text'].includes(section.type)) {
                if (processedTypes.has(section.type)) continue; // Skip duplicates (fragments)
                processedTypes.add(section.type);

                const items = section.type === 'experience' ? resumeData.workHistory : 
                              section.type === 'education' ? resumeData.education : 
                              (resumeData.customFields && resumeData.customFields.length > 0 ? resumeData.customFields.map(f => ({ name: f.name, value: f.value })) : (section.content?.text ? [{ name: '', value: section.content.text, text: section.content.text }] : []));

                // Create Base Chunk
                let currentChunk: CVSection = { ...section, id: section.id, content: { ...section.content, items: [] } };
                // Keep Original ID for first chunk to preserve selection state if possible

                // Iterate Items
                for (const item of items) {
                    const itemH = estimateItemHeight(item, section.type);

                    // Header overhead for a new chunk with title
                    const titleOverhead = (currentChunk.content.items.length === 0 && currentChunk.title && !currentChunk.settings?.hideTitle) ? 60 : 0;

                    const availableSpace = SAFE_HEIGHT - currentHeight;

                    // Standard Overflow check
                    if (currentHeight + itemH + titleOverhead > SAFE_HEIGHT && currentHeight > 100) {

                        // CONTENT SPLITTING LOGIC
                        // Only split if we have a substantial description and enough space to make it worth it (>150px)
                        // Handle Role splitting (less common, but possible if bullet points are long)
                        if (section.type === 'experience' && (item as any).role && (item as any).role.length > 200 && availableSpace > 150) {
                            const textSpace = availableSpace - 60; // Estimate char capacity: ~3.5 chars per pixel height (from estimateItemHeight)
                            // available pixels for text = availableSpace - base_overhead (60)
                            // const textSpace = availableSpace - 60; // This line was duplicated in the instruction, keeping one.
                            if (textSpace > 50) {
                                const approximateChars = Math.floor(textSpace * 3.0); // Conservative estimate

                                // Look for a split point (newline or period) within the last 20% of the limit
                                // to maximize usage without cutting mid-sentence too abruptly
                                const searchRangeStart = Math.max(0, approximateChars - 100);
                                const searchRangeEnd = approximateChars;

                                const text = (item as any).description;
                                let splitIndex = -1;

                                // Priority 1: Newlines (Paragraphs)
                                const lastNewline = text.lastIndexOf('\n', searchRangeEnd);
                                if (lastNewline > searchRangeStart) {
                                    splitIndex = lastNewline;
                                } else {
                                    // Priority 2: Period (Sentences)
                                    const lastPeriod = text.lastIndexOf('. ', searchRangeEnd);
                                    if (lastPeriod > searchRangeStart) {
                                        splitIndex = lastPeriod + 1; // Include the period
                                    }
                                }

                                if (splitIndex > 0) {
                                    const part1Text = text.substring(0, splitIndex).trim();
                                    const part2Text = text.substring(splitIndex).trim();

                                    // Add Part 1 to Current Page
                                    currentChunk.content.items.push({ ...item, description: part1Text });
                                    newPages[currentPageIndex].push(currentChunk);

                                    // New Page for Part 2
                                    newPages.push([]);
                                    currentPageIndex++;
                                    currentHeight = 0;

                                    // Start New Chunk for Part 2
                                    // Part 2 inherits role/company but maybe we mark it as continued?
                                    // User req: "cut section... so it continu on the pages"
                                    // Usually we want to hide dates/role on the second part to avoid duplication?
                                    // Or maybe show "Role (vervolg)"?
                                    // For now, let's keep role/company identical so it looks like one block if they are close, 
                                    // or maybe simply hide them if we can.
                                    // But CVSectionItem doesn't support hiding fields easily without schema change.
                                    // Let's just push the item. 

                                    currentChunk = {
                                        ...section,
                                        id: `${section.type}-chunk-${currentPageIndex}-${Math.random().toString().slice(2, 6)}`,
                                        content: { ...section.content, items: [] },
                                        settings: { ...(section.settings || {}), hideTitle: true }
                                    };

                                    // Push Part 2 as the first item of new chunk
                                    // Note: we set currentHeight based on Part 2's size
                                    const part2Item = { ...item, description: part2Text, role: `${(item as any).role} (vervolg)`, period: '' };
                                    currentChunk.content.items.push(part2Item);

                                    const part2H = estimateItemHeight(part2Item, section.type);
                                    currentHeight += part2H; // No title overhead because hideTitle is true
                                    continue; // Move to next item in original list
                                }
                            }
                        }

                        // Fallback: Move entire item to next page (Existing Logic)
                        if (currentChunk.content.items.length > 0) {
                            newPages[currentPageIndex].push(currentChunk);
                        }

                        newPages.push([]);
                        currentPageIndex++;
                        currentHeight = 0;

                        currentChunk = {
                            ...section,
                            id: `${section.type}-chunk-${currentPageIndex}-${Math.random().toString().slice(2, 6)}`,
                            content: { ...section.content, items: [] },
                            settings: { ...(section.settings || {}), hideTitle: true }
                        };
                    }

                    currentChunk.content.items.push(item);
                    currentHeight += itemH + (currentChunk.content.items.length === 1 ? titleOverhead : 0);
                }

                if (currentChunk.content.items.length > 0) {
                    newPages[currentPageIndex].push(currentChunk);
                }

            } else {
                // Standard Section handling
                let estimatedHeight = 100;
                const el = document.getElementById(`section-${section.id}`);

                if (el && el.offsetHeight > 0) {
                    estimatedHeight = el.offsetHeight + design.itemSpacing;
                } else {
                    // Heuristics
                    if (section.type === 'header') estimatedHeight = 150;
                    if (section.type === 'summary') estimatedHeight = 100 + ((section.content?.text?.length || 0) / 3);
                    if (section.type === 'skills') estimatedHeight = 100 + ((section.content?.items?.length || 0) * 10);
                }

                if (currentHeight + estimatedHeight > SAFE_HEIGHT && currentHeight > 100) {
                    newPages.push([section]);
                    currentPageIndex++;
                    currentHeight = estimatedHeight;
                } else {
                    newPages[currentPageIndex].push(section);
                    currentHeight += estimatedHeight;
                }
            }
        }

        // Final cleanup & Update
        if (newPages.length === 0) newPages.push([]);

        // Check if structure changed significantly
        // We always update if we processed lists, because items might have changed
        // To avoid infinite loop (repaginate -> setPage -> useEffect -> repaginate)
        // We need stable IDs if nothing changed. 
        // Our ID generation uses Math.random(). This will cause loop if we are not careful.
        // We should compare serialized content?
        // Or simply: check if the NUMBER of pages or mapping changed?

        // If we setPages, it triggers useEffect. 
        // We added resumeData dependency.
        // If items changed, we MUST update. 
        // If items didn't change, we shouldn't update if outcome is same.
        // But random IDs make outcome different.

        // FIX: Only setPages if the content structure (count of sections/pages) differs 
        // OR if deep content check fails.
        // Simplified: Checking flattened IDs prevents loop IF we reuse IDs.
        // But we generate new IDs.

        // This is risky. Let's use deterministic IDs for chunks.
        // ID = originalID + suffix.

        // Refined ID logic in loop:
        // id: `${section.id}-part-${currentPageIndex}`

        // Re-run the loop with deterministic ID?
        // See implementation above.

        // Let's blindly setPages for now but maybe debounce? 
        // The setTimeout(500) acts as debounce.

        // Avoid loop: compare `JSON.stringify(newPages.map(p => p.map(s => s.id)))` vs old.
        // If we use stable suffixes, this works.

        setPages(newPages);
    };
    const handleAddSection = (type: CVSection['type']) => {
        recordHistory();

        // FIX: For List-type sections (experience, education), check if one exists first.
        // If it exists, we likely want to specific logic or just ensure we don't duplicate if not intended.
        // Actually, the user request says "error add werkervaring".
        // The issue is likely that `resumeData` is the source of truth for these lists,
        // but `pages` structure renders them.
        // If we add a section here, we might be desyncing or duplicating.

        // Correct Logic:
        // 1. If type is 'experience' or 'education', we should add a NEW ITEM to `resumeData`
        //    AND ensure the section exists in `pages`.
        // 2. If valid section exists, just adding item to `resumeData` + repaginate should work
        //    (because repaginate reconstructs pages from resumeData).

        if (type === 'experience') {
            setResumeData(prev => ({
                ...prev,
                workHistory: [...prev.workHistory, { role: 'Nieuwe Functie', company: 'Nieuw Bedrijf', period: '202X - Heden', description: 'Beschrijving...' }]
            }));
            // We don't need to manually push to pages if repaginate handles it?
            // `repaginate` uses `resumeData` to rebuild experience sections.
            // So just triggering repaginate is enough IF the section is already "active" or if repaginate always generates it from data.
            // Repaginate iterates `allSections` (current pages) to find 'experience'. 
            // If the section was deleted, we need to restore it.

            // Let's check if we have an experience section
            const hasExp = pages.flat().some(s => s.type === 'experience');
            if (!hasExp) {
                // We need to add the container section
                const newSection: CVSection = { id: Math.random().toString(), type: 'experience', width: 100, content: { items: [] }, title: 'Werkervaring' };
                setPages(prev => {
                    const newPages = [...prev];
                    if (newPages.length === 0) newPages.push([]);
                    newPages[newPages.length - 1].push(newSection);
                    return newPages;
                });
            }
        } else if (type === 'education') {
            setResumeData(prev => ({
                ...prev,
                education: [...prev.education, { school: 'Nieuwe School', degree: 'Nieuwe Opleiding', year: '202X' }]
            }));
            const hasEdu = pages.flat().some(s => s.type === 'education');
            if (!hasEdu) {
                const newSection: CVSection = { id: Math.random().toString(), type: 'education', width: 100, content: { items: [] }, title: 'Opleiding' };
                setPages(prev => {
                    const newPages = [...prev];
                    if (newPages.length === 0) newPages.push([]);
                    newPages[newPages.length - 1].push(newSection);
                    return newPages;
                });
            }
        } else {
            // Standard Sections (One-off)
            const newSection: CVSection = {
                id: Math.random().toString(),
                type,
                width: 100,
                content: {}
            };
            if (type === 'header') { newSection.content = { name: 'Naam', role: 'Functie', email: 'email@example.com', phone: '06-12345678' }; newSection.settings = { showEmail: true, showPhone: true, reverseOrder: false }; }
            if (type === 'summary') { newSection.title = 'Profielschets'; newSection.content = { text: 'Plaats hier een korte samenvatting...' }; }
            if (type === 'skills') { newSection.title = 'Vaardigheden'; newSection.content = { items: ['Skill 1', 'Skill 2'] }; }
            if (type === 'custom_text') { newSection.content = { text: 'Vrije tekst...' }; }

            setPages(prev => {
                const newPages = [...prev];
                if (newPages.length === 0) newPages.push([]);
                newPages[newPages.length - 1].push(newSection);
                return newPages;
            });
        }
    };

    const handleLoadTemplate = (template: CVTemplate) => {
        recordHistory();
        setDesign({ ...DEFAULT_DESIGN, ...template.design });
        if (template.structure) {
            const rawStructure = JSON.parse(JSON.stringify(template.structure));
            const newPages = mergeDataIntoStructure(rawStructure, resumeData);
            setPages(newPages);
        }
        if (template.decorations) setDecorations(template.decorations); else setDecorations([]);
        setCurrentTemplateId(template.id);
        setTemplateName(template.name);
        setShowTemplates(false);
    };

    const handleGenerateTailoredCV = async () => {
        if (!selectedVacancyId) return;
        const vac = vacancies.find(v => v.id === selectedVacancyId);
        if (!vac) return;
        setIsAiProcessing(true);
        try {
            const currentProfile = {
                name: initialData?.personalInfo.name || '',
                role: initialData?.title || '',
                summary: initialData?.summary || '',
                skills: initialData?.skills || [],
                timeline: initialData?.workHistory || []
            };
            const result = await generateTailoredCV(currentProfile, vac, customAiPrompt);
            setTailorResult(result);
        } catch (error) {
            console.error("Tailoring failed", error);
            alert("Kon CV niet optimaliseren.");
        } finally {
            setIsAiProcessing(false);
        }
    };

    const applyTailoredChange = (field: 'summary' | 'experience') => {
        recordHistory();
        const newPages = [...pages];
        if (field === 'summary' && tailorResult.tailoredSummary) {
            for (let p = 0; p < newPages.length; p++) {
                const summarySec = newPages[p].find(s => s.type === 'summary');
                if (summarySec) { summarySec.content.text = tailorResult.tailoredSummary; break; }
            }
            setResumeData(prev => ({ ...prev, summary: tailorResult.tailoredSummary }));
        } else if (field === 'experience' && tailorResult.tailoredTimeline) {
            for (let p = 0; p < newPages.length; p++) {
                const expSec = newPages[p].find(s => s.type === 'experience');
                if (expSec) {
                    expSec.content.items = tailorResult.tailoredTimeline.map((item: any) => ({ role: item.role, company: item.company, period: item.year, description: item.description }));
                    break;
                }
            }
            setResumeData(prev => ({ ...prev, workHistory: tailorResult.tailoredTimeline.map((t: any) => ({ role: t.role, company: t.company, period: t.year, description: t.description })) }));
        }
        setPages(newPages);
    };

    const toggleShowOriginal = (index: number) => {
        setShowOriginalFor(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const applyTailoredWorkItem = (index: number, tailoredItem: any) => {
        recordHistory();
        const newPages = [...pages];

        // Deep clone pages to be safe for nested mutation
        const deepPages = JSON.parse(JSON.stringify(newPages));

        let found = false;
        // Find experience section and update specific item
        for (let p = 0; p < deepPages.length; p++) {
            const expSec = deepPages[p].find((s: any) => s.type === 'experience');
            if (expSec && expSec.content.items && expSec.content.items[index]) {
                expSec.content.items[index] = {
                    ...expSec.content.items[index],
                    description: tailoredItem.description,
                    role: tailoredItem.role // update role too
                };
                found = true;
                break;
            }
        }

        if (found) {
            setPages(deepPages);
            setResumeData(prev => {
                const newHistory = [...(prev.workHistory || [])];
                if (newHistory[index]) {
                    newHistory[index] = {
                        ...newHistory[index],
                        description: tailoredItem.description,
                        role: tailoredItem.role
                    };
                }
                return { ...prev, workHistory: newHistory };
            });
        }
    };

    const addDecoration = (type: CVDecoration['type']) => {
        recordHistory();
        // Calculate current visible page index
        let currentPageIndex = 0;
        if (containerRef.current) {
            const scrollTop = containerRef.current.scrollTop;
            const pageHeight = A4_HEIGHT_PX * zoomLevel + 32; // Height + margin
            currentPageIndex = Math.floor(scrollTop / pageHeight);
        }

        // Ensure we don't exceed page count
        if (currentPageIndex >= pages.length) currentPageIndex = pages.length - 1;
        if (currentPageIndex < 0) currentPageIndex = 0;

        const newDeco: CVDecoration = {
            id: Math.random().toString(),
            type,
            x: 100, // Default slightly inset
            y: 100,
            width: 100,
            height: 100,
            opacity: 1,
            zIndex: 50, // Ensure visibility on top of content
            pageIndex: currentPageIndex,
            rotation: 0,
            color: type === 'circle' || type === 'square' || type === 'line' ? design.primaryColor : undefined,
            text: type === 'text' ? 'Dubbelklik om te bewerken' : undefined
        };
        setDecorations(prev => [...prev, newDeco]);
        setSelectedDecoId(newDeco.id);
    };

    const updateDecoration = (id: string, updates: Partial<CVDecoration>) => { setDecorations(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d)); };
    const updateSectionStyle = (pageIdx: number, secId: string, styleKey: string, styleVal: string | number) => {
        const page = pages[pageIdx];
        const sec = page.find(s => s.id === secId);
        if (sec) {
            const newStyles = { ...(sec.styles || {}), [styleKey]: styleVal };
            handleSectionUpdate(pageIdx, secId, { styles: newStyles });
        }
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setIsUploadingImage(true);
            try {
                const url = await supabase_upload_image(e.target.files[0]);
                if (url) {
                    recordHistory();

                    // Same page calculation logic as addDecoration
                    let currentPageIndex = 0;
                    if (containerRef.current) {
                        const scrollTop = containerRef.current.scrollTop;
                        const pageHeight = A4_HEIGHT_PX * zoomLevel + 32;
                        currentPageIndex = Math.floor(scrollTop / pageHeight);
                    }
                    if (currentPageIndex >= pages.length) currentPageIndex = pages.length - 1;
                    if (currentPageIndex < 0) currentPageIndex = 0;

                    const newDeco: CVDecoration = {
                        id: Math.random().toString(),
                        type: 'image',
                        x: 100, y: 100, width: 200, height: 200,
                        opacity: 1, zIndex: 50, pageIndex: currentPageIndex, rotation: 0,
                        imageUrl: url
                    };
                    setDecorations(prev => [...prev, newDeco]);
                    setSelectedDecoId(newDeco.id);
                }
            } catch (err: any) {
                console.error("Upload failed", err);
                alert("Afbeelding uploaden mislukt: " + (err.message || "Onbekende fout"));
            } finally {
                setIsUploadingImage(false);
                // Reset input
                if (fileInputRef.current) fileInputRef.current.value = '';
                if (sidebarFileRef.current) sidebarFileRef.current.value = '';
                if (e.target) e.target.value = '';
            }
        }
    };

    const handleCanvasDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (file.type.startsWith('image/')) {
                setIsUploadingImage(true);
                try {
                    const url = await supabase_upload_image(file);
                    if (url) {
                        recordHistory();
                        const rect = containerRef.current?.getBoundingClientRect();
                        // Logic to determine drop page index roughly
                        const scrollY = containerRef.current?.scrollTop || 0;
                        const relativeY = (e.clientY - (rect?.top || 0) + scrollY) / zoomLevel;
                        const pageHeightUnscaled = A4_HEIGHT_PX + (32 / zoomLevel); // approx margin

                        let pageIndex = Math.floor(relativeY / pageHeightUnscaled);
                        let localY = relativeY % pageHeightUnscaled;

                        if (pageIndex >= pages.length) pageIndex = pages.length - 1;

                        const newDeco: CVDecoration = {
                            id: Math.random().toString(),
                            type: 'image',
                            x: Math.max(0, (e.clientX - (rect?.left || 0)) / zoomLevel - 100),
                            y: localY - 100,
                            width: 200, height: 200,
                            opacity: 1, zIndex: 50, pageIndex: pageIndex, rotation: 0,
                            imageUrl: url
                        };
                        setDecorations(prev => [...prev, newDeco]);
                        setSelectedDecoId(newDeco.id);
                    }
                } catch (err) { console.error(err); }
                finally { setIsUploadingImage(false); }
            }
        }
    };

    const toggleTailorSection = (id: string) => {
        setExpandedTailorSections(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // ... (Keep existing save/load functions)
    const handleSaveClick = () => {
        if (mode === 'candidate') {
            if (!initialData?.candidateId && !initialData?.id) { alert("Geen kandidaat geselecteerd."); return; }
            // Always open modal to let user choose Overwrite or Save As
            setSavedVersionName(templateName); // Prefill with current name
            setShowSaveVersionModal(true);
        } else {
            handleSaveTemplate();
        }
    };
    const handleSaveTemplate = async () => { setSavingTemplate(true); try { const payload = { id: currentTemplateId === 'default' ? undefined : currentTemplateId, name: templateName, design, structure: pages, decorations, clientId: user?.clientId }; const saved = await supabase_save_cv_template(payload); setCurrentTemplateId(saved.id); setAvailableTemplates(prev => { const ex = prev.findIndex(t => t.id === saved.id); if (ex >= 0) { const up = [...prev]; up[ex] = saved; return up; } return [...prev, saved]; }); alert("Template opgeslagen!"); } catch (e: any) { alert(`Fout: ${extractErrorMessage(e)}`); } finally { setSavingTemplate(false); } };
    const handleSaveAsNewTemplate = async () => { setSavingTemplate(true); try { const newName = templateName.includes('(Copy)') ? templateName : `${templateName} (Copy)`; const payload = { name: newName, design, structure: pages, decorations, clientId: user?.clientId }; const saved = await supabase_save_cv_template(payload); setCurrentTemplateId(saved.id); setTemplateName(saved.name); setAvailableTemplates(prev => [saved, ...prev]); alert("Template gekopieerd!"); } catch (e: any) { alert(`Fout: ${extractErrorMessage(e)}`); } finally { setSavingTemplate(false); } };
    const extractCurrentData = (currentPages: CVSection[][], fallbackData: CVData): CVData => { const newData = JSON.parse(JSON.stringify(fallbackData)); currentPages.flat().forEach(sec => { if (!sec.content) return; if (sec.type === 'header') { if (sec.content.name) newData.personalInfo.name = sec.content.name; if (sec.content.role) newData.title = sec.content.role; } if (sec.type === 'summary') newData.summary = sec.content.text; }); return newData; };

    const handleDetailsChange = (newData: CVData) => {
        setResumeData(newData);

        // SYNC TO PAGES (Visual Update)
        const newPages = [...pages];

        newPages.forEach((page, pageIndex) => {
            page.forEach((section) => {
                if (section.type === 'header') {
                    if (section.content) {
                        section.content.name = newData.personalInfo.name;
                        section.content.role = newData.title;
                        section.content.email = newData.personalInfo.email;
                        section.content.phone = newData.personalInfo.phone;
                        // Add location/linkedin if supported in header content structure
                    }
                }
                if (section.type === 'summary') {
                    if (section.content) section.content.text = newData.summary;
                }
                if (section.type === 'skills') {
                    if (section.content) section.content.items = newData.skills;
                }
                // NOTE: Experience and Education are handled by repaginate() via resumeData dependency
                // We do NOT update them here to avoid overwriting the split logic.

            });
        });

        setPages(newPages);
    };

    const handleSaveVersion = async (name: string, forceOverwrite: boolean = false) => {
        if (!name) { alert("Geef een naam op voor deze versie."); return; }
        setSavingTemplate(true);
        try {
            const content = extractCurrentData(pages, resumeData);
            const snap = { design, structure: pages, decorations, content };
            const cid = initialData?.candidateId || initialData?.id;

            if (!cid) throw new Error("Geen Kandidaat ID gevonden.");

            // Check if name exists (excluding current ID if we are editing an existing template/version)
            const isDuplicate = await import('../services/supabaseService').then(m => m.supabase_check_cv_version_name(cid, name, initialTemplate?.id));

            if (isDuplicate) {
                if (!forceOverwrite) {
                    setPendingSaveName(name);
                    setShowOverwriteWarning(true);
                    setSavingTemplate(false);
                    return; // Stop and wait for user explicitly selecting in modal
                } else {
                    // Find the ID of the existing version to overwrite
                    const { data: existing } = await import('../services/supabaseService').then(async m => {
                        const { data } = await m.supabase.from('applications').select('id').eq('id', cid).eq('cv_summary', name).single();
                        return { data };
                    });

                    if (existing) {
                        const suc = await import('../services/supabaseService').then(m => m.supabase_update_cv_version(existing.id, content, name, snap));
                        if (suc) {
                            alert("Versie overschreven!");
                            setShowSaveVersionModal(false);
                            setShowOverwriteWarning(false);
                            setPendingSaveName('');
                        }
                        else throw new Error("Overschrijven mislukt.");
                        return;
                    }
                }
            }

            // Normal Save (New or Update Current if not changing name)
            // Logic: 
            // 1. If we have initialTemplate.id (editing existing) AND name is same -> Update
            // 2. If we have initialTemplate.id (editing existing) AND name is DIFFERENT -> We already checked duplicate. If unique, Create New? Or Rename?
            //    Standard 'Save As' behavior usually implies creating new if name changed, OR renaming. 
            //    The prompt asked for "Save as current file and replace, or same name is not possible".
            //    So if name equals current name -> UPDATE.
            //    If name is new -> INSERT.

            if (initialTemplate?.id && name === initialTemplate.name) {
                const suc = await import('../services/supabaseService').then(m => m.supabase_update_cv_version(initialTemplate.id, content, name, snap));
                if (suc) { alert("Versie bijgewerkt!"); setShowSaveVersionModal(false); }
                else throw new Error("Bijwerken mislukt.");
            } else {
                // Create NEW
                const suc = await supabase_save_cv_version(cid, null, content, name, snap);
                if (suc) { alert("Nieuwe versie opgeslagen!"); setShowSaveVersionModal(false); }
                else throw new Error("Opslaan mislukt.");
            }

        } catch (e: any) {
            alert(`Fout bij opslaan: ${extractErrorMessage(e)}`);
        } finally {
            setSavingTemplate(false);
        }
    };


    const generatePDFBlob = async (): Promise<Blob> => {
        const input = document.querySelector('.cv-pages-wrapper') as HTMLElement;
        if (!input) throw new Error("Could not find CV DOM");

        const previousZoom = zoomLevel;
        setZoomLevel(1);

        await new Promise(resolve => setTimeout(resolve, 800));

        try {
            const html2canvas = (await import('html2canvas')).default;
            const jsPDF = (await import('jspdf')).default;

            const pages = document.querySelectorAll('.cv-page');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i] as HTMLElement;

                const canvas = await html2canvas(page, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: design.backgroundColor,
                    ignoreElements: (element) => element.classList.contains('no-print') || element.classList.contains('no-print-bg'),
                    onclone: (clonedDoc) => {
                        clonedDoc.querySelectorAll('.cursor-nwse-resize').forEach(h => h.remove());
                        clonedDoc.querySelectorAll('.cv-section-wrapper').forEach(s => {
                            (s as HTMLElement).style.border = 'none';
                            (s as HTMLElement).style.borderColor = 'transparent';
                        });
                    }
                });

                const imgData = canvas.toDataURL('image/png');
                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            }

            return pdf.output('blob');
        } finally {
            setZoomLevel(previousZoom);
        }
    };

    const handleDownloadPDF = async () => {
        setSavingTemplate(true);
        try {
            const blob = await generatePDFBlob();
            if (!blob) throw new Error('PDF Error');
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${templateName.replace(/\s+/g, '_')}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e: any) {
            alert(e.message);
        } finally {
            setSavingTemplate(false);
        }
    };



    const generateCloudConvertedBlob = async (format: 'docx' | 'pdf'): Promise<Blob> => {
        const input = document.querySelector('.cv-pages-wrapper') as HTMLElement;
        if (!input) throw new Error("Could not find CV DOM");

        const clonedNode = input.cloneNode(true) as HTMLElement;
        clonedNode.querySelectorAll('.cursor-nwse-resize').forEach(h => h.remove());
        clonedNode.querySelectorAll('.cv-section-wrapper').forEach(s => {
             (s as HTMLElement).style.border = 'none';
             (s as HTMLElement).style.borderColor = 'transparent';
        });

        const html = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <script src="https://cdn.tailwindcss.com"></script>
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=${design.fontFamily.replace(/ /g, '+')}&display=swap');
                    body { font-family: '${design.fontFamily}', sans-serif; background: white !important; margin: 0; padding: 0; }
                    .cv-page { width: 100% !important; min-height: 297mm; margin: 0 !important; background: white !important; page-break-after: always; position: relative; overflow: hidden; }
                    .cv-pages-wrapper { transform: none !important; padding: 0 !important; background: white !important; }
                    * { box-sizing: border-box; }
                    ul li::marker { color: ${design.bulletColor || design.primaryColor}; }
                    .no-print { display: none !important; }
                </style>
            </head>
            <body>
                ${clonedNode.outerHTML}
            </body>
            </html>
        `;

        const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
        const response = await fetch(`${apiUrl}/api/convert/html-to-document`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ html, format })
        });

        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || "Conversie mislukt");
        }

        return await response.blob();
    };

    const handleCloudConvert = async (format: 'docx' | 'pdf', uploadToM365: boolean = false) => {
        setSavingTemplate(true);
        const newTab = uploadToM365 ? window.open('', '_blank') : null;
        if (newTab) newTab.document.write(`<html><body style="font-family: sans-serif; display: flex; justify-content: center; align-items: center; height: 100vh; background: #f8fafc; color: #334155;"><h2>Document wordt gegenereerd (${format.toUpperCase()}) en geÃ¼pload naar M365...</h2></body></html>`);

        try {
            const blob = await generateCloudConvertedBlob(format);
            if (!blob) throw new Error("Kon document niet genereren");

            const ext = format === 'docx' ? 'docx' : 'pdf';
            const cleanName = templateName.replace(/[\\/:*?"<>|]/g, '').trim() || 'Document';
            const fileName = `${cleanName} CV.${ext}`;

            if (uploadToM365) {
                const mimeType = format === 'docx' 
                    ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' 
                    : 'application/pdf';
                
                const { uploadToOneDrive } = await import('../services/microsoftGraphService');
                const docInfo = await uploadToOneDrive(blob, fileName, 'Kandidaten CVs', mimeType);

                if (docInfo && docInfo.webUrl) {
                    if (newTab) newTab.location.href = docInfo.webUrl;
                    else window.open(docInfo.webUrl, '_blank');
                } else if (docInfo && docInfo.error) {
                    const errMsg = typeof docInfo.error === 'string' ? docInfo.error : (docInfo.error.message || "Onbekende fout");
                    throw new Error(errMsg.includes('authentication') ? "Microsoft account is mogelijk niet gekoppeld. Zorg dat je bent ingelogd met Microsoft." : errMsg);
                } else {
                    throw new Error("Geen webUrl ontvangen");
                }
            } else {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = fileName;
                a.click();
                URL.revokeObjectURL(url);
                if (newTab) newTab.close();
            }
        } catch (e: any) {
            console.error('Convert Error:', e);
            if (newTab) newTab.close();
            alert("Export mislukt: " + e.message);
        } finally {
            setSavingTemplate(false);
        }
    };

    const handleGenerateProWord = async (openInWordDesktop = false) => {
        if (!selectedProTemplateId) return;
        
        setIsGeneratingWord(true);
        try {
            const cid = initialData?.candidateId || initialData?.id;
            if (!cid) throw new Error("Geen kandidaat geselecteerd.");

            // 1. Send generation request
            const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
            const response = await fetch(`${apiUrl}/api/documents/generate-cv`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    candidateId: cid,
                    templateId: selectedProTemplateId,
                    userId: user?.id,
                    title: `Word Export - ${templateName}`,
                    pages: pages // Pass the extracted HTML content blocks
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || "Fout bij genereren Word document.");
            }

            const doc = await response.json();

            // 2. We got the doc record. Now generate a signed URL
            const { supabase } = await import('../services/supabaseService');
            
            if (openInWordDesktop) {
                // Generate long-lived signed URL for Office Desktop to read/write
                const { data: signedData, error: signError } = await supabase
                    .storage
                    .from('documents')
                    .createSignedUrl(doc.storage_path, 86400 * 30); // Valid for 30 days
                    
                if (signError || !signedData) throw new Error("Kon geen toegang krijgen tot het gegenereerde bestand.");
                
                // Tricky part: Office URI Scheme `ms-word:ofe|u|URL` expects a direct public/authenticated URL.
                // Supabase signed URLs CAN work. Let's try it.
                // We use 'ofe' (Open For Edit). You could use 'ofv' (Open For View).
                const wordUri = `ms-word:ofe|u|${signedData.signedUrl}`;
                
                // Create an invisible iframe or direct location change to trigger the protocol handler
                window.location.href = wordUri;
                
            } else {
                // Just Download the file locally
                const { data: downloadData, error: downloadError } = await supabase
                    .storage
                    .from('documents')
                    .createSignedUrl(doc.storage_path, 3600, { download: true });
                    
                if (downloadError || !downloadData) throw new Error("Kon document niet downloaden.");
                
                const a = document.createElement('a');
                a.href = downloadData.signedUrl;
                a.download = `${templateName.replace(/\s+/g, '_')}.docx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }

        } catch (e: any) {
            console.error("Pro Word Gen Error:", e);
            alert("Export mislukt: " + e.message);
        } finally {
            setIsGeneratingWord(false);
        }
    };


    const execCmd = (cmd: string, val?: string) => { document.execCommand(cmd, false, val); };
    const insertSnippet = (html: string) => { setShowTemplates(false); document.execCommand('insertHTML', false, html); };
    const preventFocusLoss = (e: React.MouseEvent) => { e.preventDefault(); };
    const handleSectionDragStart = (e: React.DragEvent, pageIndex: number, sectionIndex: number) => { setDraggedSection({ pageIndex, sectionIndex }); e.dataTransfer.setData('section', JSON.stringify({ pageIndex, sectionIndex })); e.dataTransfer.effectAllowed = 'move'; };
    const handleSectionDrop = (e: React.DragEvent, targetPageIndex: number, targetSectionIndex: number) => { e.preventDefault(); if (!draggedSection) return; const newPages = [...pages]; const sourcePage = newPages[draggedSection.pageIndex]; const sectionToMove = sourcePage[draggedSection.sectionIndex]; newPages[draggedSection.pageIndex].splice(draggedSection.sectionIndex, 1); newPages[targetPageIndex].splice(targetSectionIndex, 0, sectionToMove); setPages(newPages); setDraggedSection(null); recordHistory(); };
    const handleSectionUpdate = (pageIndex: number, sectionId: string, updates: Partial<CVSection>) => { const newPages = [...pages]; const section = newPages[pageIndex].find(s => s.id === sectionId); if (section) { Object.assign(section, updates); setPages(newPages); recordHistory(); } };

    return (
        <div className="flex h-screen bg-slate-100 overflow-hidden font-sans">
            <style>{`
         @media print {
           @page { margin: 0; }
           body { visibility: hidden; background: white; margin: 0; padding: 0; }
           body * { visibility: hidden; }
           .cv-print-area { 
             visibility: visible !important; 
             position: absolute; 
             left: 0; 
             top: 0; 
             width: 100%; 
             margin: 0; 
             padding: 0;
             display: block !important;
             overflow: visible !important;
             height: auto !important;
             transform: none !important;
           }
           .cv-print-area * { visibility: visible !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
           .cv-page {
             break-inside: avoid;
             break-after: page; 
             page-break-after: always;
             margin: 0;
             box-shadow: none !important;
             border: none !important;
             width: 100% !important;
             height: 1123px !important; /* Force A4 height */
             position: relative !important;
             overflow: hidden !important;
           }
           /* #root > *:not(.cv-page-container) { display: none !important; } REMOVED TO FIX BLANK PAGE */
           .no-print { display: none !important; }
         }
       /* Bullet Color */
       .cv-page ul li::marker { color: ${design.bulletColor || design.primaryColor}; }
       `}</style>

            {/* Left Toolbar */}
            <div className="w-16 bg-white border-r border-slate-200 flex flex-col items-center py-4 gap-4 z-20 shadow-sm no-print">
                <button onClick={onBack} className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 transition-colors"><ArrowLeft size={20} /></button>
                {portalProposalAttachMode && onAttachPortalProposal ? (
                    <>
                        <div className="h-px w-8 bg-slate-200" />
                        <button
                            type="button"
                            onClick={() => {
                                onAttachPortalProposal({
                                    version: 1,
                                    attachedAt: new Date().toISOString(),
                                    design: JSON.parse(JSON.stringify(design)),
                                    pages: JSON.parse(JSON.stringify(pages)),
                                    decorations: JSON.parse(JSON.stringify(decorations)),
                                    resumeData: JSON.parse(JSON.stringify(resumeData)),
                                });
                            }}
                            className="p-2 rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                            title="CV koppelen aan klantportaal-voorstel"
                        >
                            <LinkIcon size={18} />
                        </button>
                    </>
                ) : null}
                <div className="h-px w-8 bg-slate-200" />
                {[{ id: 'design', icon: <Palette size={20} />, label: 'Design' }, { id: 'structure', icon: <Layout size={20} />, label: 'Structuur' }, { id: 'elements', icon: <Plus size={20} />, label: 'Add' }, { id: 'uploads', icon: <Upload size={20} />, label: 'Upload' }, { id: 'ai_chat', icon: <Bot size={20} />, label: 'AI' }, { id: 'translate', icon: <Languages size={20} />, label: 'Vertaal' }, ...(mode === 'candidate' ? [{ id: 'cv_tailor', icon: <Briefcase size={20} />, label: 'Tailor' }] : [])].map(tool => (
                    <button key={tool.id} onClick={() => { setActiveToolTab(tool.id as any); if (tool.id === 'cv_tailor') setIsTailoring(true); else setIsTailoring(false); }} className={`p-3 rounded-xl transition-all relative group ${activeToolTab === tool.id ? 'bg-slate-900 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100'}`}>
                        {tool.icon}
                        <span className="absolute left-14 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">{tool.label}</span>
                    </button>
                ))}
                <div className="h-px w-8 bg-slate-200" />
                <button
                    onClick={() => setIsPreviewMode(!isPreviewMode)}
                    className={`p-3 rounded-xl transition-all relative group ${isPreviewMode ? 'bg-purple-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100'}`}
                >
                    {isPreviewMode ? <EyeOff size={20} /> : <Eye size={20} />}
                    <span className="absolute left-14 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                        {isPreviewMode ? 'Bewerk Modus' : 'Voorbeeld Modus'}
                    </span>
                </button>
                <div className="mt-auto flex flex-col gap-4">
                    <button onClick={handleSaveClick} className="p-3 bg-[#e51b23] text-white rounded-xl shadow-lg hover:scale-105 transition-transform">{savingTemplate ? <Loader2 size={20} className="animate-spin" /> : <Save size={20} />}</button>
                </div>
            </div>

            {/* Sub Sidebar */}
            {!isTailoring && (
                <div className="w-72 bg-white border-r border-slate-200 flex flex-col z-10 animate-slideRight no-print">
                    <div className="p-4 border-b border-slate-200 flex items-center justify-between"><h3 className="font-black text-slate-900 uppercase tracking-tight text-sm">Tools</h3></div>
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                        {activeToolTab === 'design' && (
                            <div className="space-y-6">
                                <div><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Font Family</label><select value={design.fontFamily} onChange={(e) => { recordHistory(); setDesign({ ...design, fontFamily: e.target.value }) }} className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold outline-none">{GOOGLE_FONTS.map(f => <option key={f.name} value={f.family}>{f.name}</option>)}</select></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Primary</label><div className="flex items-center gap-2"><input type="color" value={design.primaryColor} onChange={(e) => { recordHistory(); setDesign({ ...design, primaryColor: e.target.value }) }} className="w-8 h-8 rounded cursor-pointer border-0 p-0" /><span className="text-[10px] text-slate-500">{design.primaryColor}</span></div></div>
                                    <div><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Text</label><div className="flex items-center gap-2"><input type="color" value={design.textColor} onChange={(e) => { recordHistory(); setDesign({ ...design, textColor: e.target.value }) }} className="w-8 h-8 rounded cursor-pointer border-0 p-0" /><span className="text-[10px] text-slate-500">{design.textColor}</span></div></div>
                                    <div><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Accent / Bij</label><div className="flex items-center gap-2"><input type="color" value={design.secondaryColor || '#ffffff'} onChange={(e) => { recordHistory(); setDesign({ ...design, secondaryColor: e.target.value }) }} className="w-8 h-8 rounded cursor-pointer border-0 p-0" /><span className="text-[10px] text-slate-500">{design.secondaryColor || '#------'}</span></div></div>
                                    <div><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Bullets</label><div className="flex items-center gap-2"><input type="color" value={design.bulletColor || design.primaryColor} onChange={(e) => { recordHistory(); setDesign({ ...design, bulletColor: e.target.value }) }} className="w-8 h-8 rounded cursor-pointer border-0 p-0" /><span className="text-[10px] text-slate-500">{design.bulletColor || design.primaryColor}</span></div></div>
                                    <div className="col-span-2"><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Achtergrond</label><div className="flex items-center gap-2"><input type="color" value={design.backgroundColor} onChange={(e) => { recordHistory(); setDesign({ ...design, backgroundColor: e.target.value }) }} className="w-full h-8 rounded cursor-pointer border-0 p-0" /><span className="text-[10px] text-slate-500">{design.backgroundColor}</span></div></div>

                                    <div className="col-span-2 pt-2 border-t border-slate-100 mt-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Sectie Titel Positie</label>
                                        <select value={(design as any).sectionTitleLayout || 'top'} onChange={(e) => { recordHistory(); setDesign({ ...design, sectionTitleLayout: e.target.value } as any) }} className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold outline-none mb-2">
                                            <option value="top">Boven (Standaard)</option>
                                            <option value="left">Links (Kolom)</option>
                                        </select>

                                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Layout Style</label>
                                        <select value={design.layoutVariant || 'classic'} onChange={(e) => { recordHistory(); setDesign({ ...design, layoutVariant: e.target.value as any }) }} className="w-full p-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-bold outline-none">
                                            <option value="classic">Klassiek (Standaard)</option>
                                            <option value="modern">Modern (Datum Boven)</option>
                                            <option value="compact">Compact (Minder Witruimte)</option>
                                        </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Tag BG</label><div className="flex items-center gap-2"><input type="color" value={design.tagColor || '#e2e8f0'} onChange={(e) => { recordHistory(); setDesign({ ...design, tagColor: e.target.value }) }} className="w-8 h-8 rounded cursor-pointer border-0 p-0" /><span className="text-[10px] text-slate-500">{design.tagColor || '#e2e8f0'}</span></div></div>
                                        <div><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Tag Text</label><div className="flex items-center gap-2"><input type="color" value={design.tagTextColor || '#475569'} onChange={(e) => { recordHistory(); setDesign({ ...design, tagTextColor: e.target.value }) }} className="w-8 h-8 rounded cursor-pointer border-0 p-0" /><span className="text-[10px] text-slate-500">{design.tagTextColor || '#475569'}</span></div></div>
                                    </div>
                                </div>


                                <div className="space-y-3 pt-2 border-t border-slate-100"><h4 className="text-[10px] font-black text-slate-400 uppercase block">Page Margins (px)</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div><label className="text-[9px] font-bold text-slate-400 block mb-1">Top</label><input type="number" min="0" max="200" value={parseInt(String((design.paddingTop ?? design.pagePadding) || 0))} onChange={(e) => { const val = parseInt(e.target.value); recordHistory(); setDesign({ ...design, paddingTop: isNaN(val) ? 0 : val }) }} className="w-full p-2 bg-slate-50 border rounded text-xs" /></div>
                                        <div><label className="text-[9px] font-bold text-slate-400 block mb-1">Bottom</label><input type="number" min="0" max="200" value={parseInt(String((design.paddingBottom ?? design.pagePadding) || 0))} onChange={(e) => { const val = parseInt(e.target.value); recordHistory(); setDesign({ ...design, paddingBottom: isNaN(val) ? 0 : val }) }} className="w-full p-2 bg-slate-50 border rounded text-xs" /></div>
                                        <div><label className="text-[9px] font-bold text-slate-400 block mb-1">Left</label><input type="number" min="0" max="200" value={parseInt(String((design.paddingLeft ?? design.pagePadding) || 0))} onChange={(e) => { const val = parseInt(e.target.value); recordHistory(); setDesign({ ...design, paddingLeft: isNaN(val) ? 0 : val }) }} className="w-full p-2 bg-slate-50 border rounded text-xs" /></div>
                                        <div><label className="text-[9px] font-bold text-slate-400 block mb-1">Right</label><input type="number" min="0" max="200" value={parseInt(String((design.paddingRight ?? design.pagePadding) || 0))} onChange={(e) => { const val = parseInt(e.target.value); recordHistory(); setDesign({ ...design, paddingRight: isNaN(val) ? 0 : val }) }} className="w-full p-2 bg-slate-50 border rounded text-xs" /></div>
                                    </div>
                                </div>
                                <div><label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Item Spacing ({design.itemSpacing}px)</label><input type="range" min="0" max="50" value={design.itemSpacing} onChange={(e) => { recordHistory(); setDesign({ ...design, itemSpacing: parseInt(e.target.value) }) }} className="w-full" /></div>
                            </div>
                        )}
                        {/* MOVED INPUT OUTSIDE CONDITIONAL BUT KEPT BUTTON TRIGGER */}
                        {activeToolTab === 'elements' && (<div className="grid grid-cols-2 gap-2">{[{ type: 'text', label: 'Text Box', icon: <Type size={16} /> }, { type: 'image', label: 'Image', icon: <ImageIcon size={16} /> }, { type: 'circle', label: 'Circle', icon: <Circle size={16} /> }, { type: 'square', label: 'Box', icon: <Square size={16} /> }, { type: 'line', label: 'Line', icon: <Minus size={16} /> }].map(el => (<button key={el.type} onClick={() => { if (el.type === 'image') fileInputRef.current?.click(); else addDecoration(el.type as any); }} className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-400 hover:bg-white transition-all gap-2">{el.icon}<span className="text-[10px] font-bold text-slate-600">{el.label}</span></button>))}</div>)}
                        {/* ALWAYS RENDER INPUT */}
                        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} />

                        {activeToolTab === 'structure' && (
                            <div className="space-y-4">
                                <p className="text-[10px] text-slate-400">Sleep of klik om secties toe te voegen aan je CV.</p>
                                <div className="space-y-2">{[{ type: 'header', label: 'Header (Naam)', icon: <User size={16} /> }, { type: 'summary', label: 'Profielschets', icon: <FileText size={16} /> }, { type: 'experience', label: 'Werkervaring', icon: <Briefcase size={16} /> }, { type: 'education', label: 'Opleiding', icon: <GraduationCap size={16} /> }, { type: 'skills', label: 'Vaardigheden', icon: <Grid3X3 size={16} /> }, { type: 'custom_text', label: 'Vrije Tekst', icon: <Type size={16} /> }].map(sec => (<button key={sec.type} onClick={() => handleAddSection(sec.type as any)} className="w-full flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-xl hover:border-red- hover:bg-red- transition-all text-left group"><div className="p-1.5 bg-slate-50 rounded text-slate-500 group-hover:bg-white group-hover:text-red-0">{sec.icon}</div><span className="text-xs font-bold text-slate-700">{sec.label}</span><Plus size={14} className="ml-auto text-slate-300 group-hover:text-red-0" /></button>))}</div>
                            </div>
                        )}
                        {activeToolTab === 'translate' && (
                            <div className="space-y-4">
                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest text-center mb-2">Vertaal volledig CV</p>
                                <p className="text-[10px] text-slate-400 text-center mb-4">Laat Froukje AI direct de taal van het CV wijzigen.</p>
                                {[
                                    { lang: "NL", label: "Nederlands" },
                                    { lang: "EN", label: "Engels" },
                                    { lang: "DE", label: "Duits" },
                                    { lang: "FR", label: "Frans" }
                                ].map((l) => (
                                    <button 
                                        key={l.lang} 
                                        onClick={() => handleTranslateCV(l.lang)}
                                        disabled={isTranslating}
                                        className="w-full bg-white border border-slate-200 p-4 rounded-xl hover:bg-purple-50 hover:border-purple-200 transition-all font-bold text-slate-700 disabled:opacity-50 flex items-center gap-3 justify-center"
                                    >
                                        {isTranslating ? <Loader2 size={16} className="animate-spin text-purple-600" /> : <Languages size={16} className="text-purple-600" />}
                                        Naar {l.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        {activeToolTab === 'edit_element' && selectedDecoId && (<div className="space-y-6">
                            <div>
                                <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Layer (Z-Index)</label>
                                <div className="flex gap-2"><button onClick={() => updateDecoration(selectedDecoId, { zIndex: (decorations.find(d => d.id === selectedDecoId)?.zIndex || 10) + 1 })} className="flex-1 p-2 bg-slate-50 rounded border text-xs hover:bg-white"><ArrowUp size={14} /> Forward</button><button onClick={() => updateDecoration(selectedDecoId, { zIndex: Math.max(0, (decorations.find(d => d.id === selectedDecoId)?.zIndex || 10) - 1) })} className="flex-1 p-2 bg-slate-50 rounded border text-xs hover:bg-white"><ArrowDown size={14} /> Backward</button></div>
                            </div>

                            {/* Color & Opacity & Placement Controls */}
                            {['circle', 'square', 'line', 'text', 'image'].includes(decorations.find(d => d.id === selectedDecoId)?.type || '') && (
                                <>
                                    {/* Color Control - Skip for Images */}
                                    {decorations.find(d => d.id === selectedDecoId)?.type !== 'image' && (
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Color</label>
                                            <div className="flex gap-2 items-center">
                                                <input type="color" className="w-8 h-8 rounded cursor-pointer border-0 p-0" value={decorations.find(d => d.id === selectedDecoId)?.color || design.primaryColor} onChange={(e) => updateDecoration(selectedDecoId, { color: e.target.value })} />
                                                <span className="text-xs text-slate-500">{decorations.find(d => d.id === selectedDecoId)?.color || design.primaryColor}</span>
                                            </div>
                                        </div>
                                    )}

                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Opacity ({Math.round((decorations.find(d => d.id === selectedDecoId)?.opacity ?? 1) * 100)}%)</label>
                                        <input type="range" min="0.1" max="1" step="0.1" className="w-full" value={decorations.find(d => d.id === selectedDecoId)?.opacity ?? 1} onChange={(e) => updateDecoration(selectedDecoId, { opacity: parseFloat(e.target.value) })} />
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Weergave</label>
                                        <select
                                            className="w-full p-2 bg-slate-50 border rounded text-xs outline-none"
                                            value={decorations.find(d => d.id === selectedDecoId)?.allPages ? 'all' : decorations.find(d => d.id === selectedDecoId)?.pageIndex}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                if (val === 'all') {
                                                    updateDecoration(selectedDecoId, { allPages: true });
                                                } else {
                                                    updateDecoration(selectedDecoId, { allPages: false, pageIndex: parseInt(val) });
                                                }
                                            }}
                                        >
                                            <option value="all">Alle Pagina's</option>
                                            {pages.map((_, idx) => (
                                                <option key={idx} value={idx}>Alleen Pagina {idx + 1}</option>
                                            ))}
                                        </select>
                                    </div>
                                </>
                            )}

                            <button onClick={() => { setDecorations(prev => prev.filter(d => d.id !== selectedDecoId)); setSelectedDecoId(null); }} className="w-full py-2 bg-red-50 text-red-600 rounded text-xs font-bold hover:bg-red-100 flex items-center justify-center gap-2"><Trash2 size={14} /> Verwijderen</button></div>)}
                        {activeToolTab === 'edit_section' && selectedSection && (
                            <div className="space-y-6">
                                <div>
                                    <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Sectie Breedte</label>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleSectionUpdate(selectedSection.pageIndex, selectedSection.sectionId, { width: 50 })} className="flex-1 p-2 bg-slate-50 rounded border text-xs hover:bg-white font-bold">50%</button>
                                        <button onClick={() => handleSectionUpdate(selectedSection.pageIndex, selectedSection.sectionId, { width: 100 })} className="flex-1 p-2 bg-slate-50 rounded border text-xs hover:bg-white font-bold">100%</button>
                                    </div>
                                </div>

                                {/* NEW PADDING CONTROLS */}
                                <div className="pt-4 border-t border-slate-100">
                                    <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Sectie Spacing (Padding)</label>
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="text-[9px] text-slate-400 block mb-1">Boven</label>
                                            <input type="number" className="w-full p-2 bg-slate-50 border rounded text-xs"
                                                value={parseInt(String((pages[selectedSection.pageIndex].find(s => s.id === selectedSection.sectionId)?.styles?.paddingTop as any) || 0))}
                                                onChange={(e) => updateSectionStyle(selectedSection.pageIndex, selectedSection.sectionId, 'paddingTop', `${e.target.value}px`)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[9px] text-slate-400 block mb-1">Onder</label>
                                            <input type="number" className="w-full p-2 bg-slate-50 border rounded text-xs"
                                                value={parseInt(String((pages[selectedSection.pageIndex].find(s => s.id === selectedSection.sectionId)?.styles?.paddingBottom as any) || 0))}
                                                onChange={(e) => updateSectionStyle(selectedSection.pageIndex, selectedSection.sectionId, 'paddingBottom', `${e.target.value}px`)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[9px] text-slate-400 block mb-1">Links</label>
                                            <input type="number" className="w-full p-2 bg-slate-50 border rounded text-xs"
                                                value={parseInt(String((pages[selectedSection.pageIndex].find(s => s.id === selectedSection.sectionId)?.styles?.paddingLeft as any) || 0))}
                                                onChange={(e) => updateSectionStyle(selectedSection.pageIndex, selectedSection.sectionId, 'paddingLeft', `${e.target.value}px`)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[9px] text-slate-400 block mb-1">Rechts</label>
                                            <input type="number" className="w-full p-2 bg-slate-50 border rounded text-xs"
                                                value={parseInt(String((pages[selectedSection.pageIndex].find(s => s.id === selectedSection.sectionId)?.styles?.paddingRight as any) || 0))}
                                                onChange={(e) => updateSectionStyle(selectedSection.pageIndex, selectedSection.sectionId, 'paddingRight', `${e.target.value}px`)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-slate-100">
                                    <label className="text-[10px] font-black text-slate-400 uppercase block mb-2">Acties</label>
                                    <button onClick={() => { const newPages = [...pages]; newPages[selectedSection.pageIndex] = newPages[selectedSection.pageIndex].filter(s => s.id !== selectedSection.sectionId); setPages(newPages); setSelectedSection(null); }} className="w-full py-2 bg-red-50 text-red-600 rounded text-xs font-bold hover:bg-red-100 flex items-center justify-center gap-2">
                                        <Trash2 size={14} /> Sectie Verwijderen
                                    </button>
                                </div>
                            </div>
                        )}
                        {activeToolTab === 'ai_chat' && (<div className="flex flex-col h-full"><div className="flex-1 overflow-y-auto mb-4 space-y-3">{chatMessages.map((m, i) => (<div key={i} className={`p-3 rounded-xl text-xs font-medium ${m.role === 'ai' ? 'bg-slate-100 text-slate-800' : 'bg-red- text-red- ml-4'}`}>{m.text}</div>))}<div ref={chatEndRef} /></div><input className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs outline-none focus:ring-2 focus:ring-red-0" placeholder="Ask Froukje..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} /></div>)}
                        {activeToolTab === 'uploads' && (
                            <div className="space-y-4">
                                <p className="text-[10px] text-slate-400">Upload afbeeldingen voor je CV.</p>
                                <button onClick={() => fileInputRef.current?.click()} className="w-full h-32 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center text-slate-400 hover:border-red-0 hover:text-red-0 hover:bg-red- transition-all gap-2">
                                    <Upload size={24} />
                                    <span className="text-xs font-bold">Kies afbeelding...</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="flex-1 bg-slate-200 overflow-hidden flex flex-col relative">
                <div className="bg-white border-b border-gray-200 p-2 flex flex-wrap items-center justify-between gap-1 shadow-inner shrink-0 relative z-40 no-print">
                    <div className="flex items-center gap-2 border-r border-slate-200 pr-2 mr-2"><input type="text" value={templateName} onChange={(e) => setTemplateName(e.target.value)} className="text-sm font-bold text-slate-800 bg-transparent outline-none w-48 placeholder:text-slate-400 focus:bg-slate-50 focus:border-slate-300 border border-transparent rounded px-2 py-1 hover:border-slate-200 transition-colors" placeholder="Document Titel" />{mode === 'candidate' && (<button onClick={() => setShowSaveVersionModal(true)} className="flex items-center gap-1.5 bg-white text-slate-600 border border-slate-200 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red- hover:text-red- transition-all shadow-sm" title="Opslaan als nieuwe versie"><FilePlus size={14} /> Opslaan als...</button>)}</div>

                    {/* View Mode Toggle */}
                    <div className="flex items-center bg-slate-100 p-0.5 rounded-lg border border-slate-200 mr-2">
                        <button onClick={() => setViewMode('paged')} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all ${viewMode === 'paged' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Design</button>
                        <button onClick={() => setViewMode('continuous')} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all ${viewMode === 'continuous' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>Content</button>
                        {mode === 'candidate' && (
                            <>
                                <button onClick={() => setViewMode('export')} className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all flex items-center gap-1 ${viewMode === 'export' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                                    <FileText size={12} /> Exporteren
                                </button>
                                <button onClick={() => setViewMode('collabora')} className={`ml-1 pl-3 border-l border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest rounded-md transition-all flex items-center gap-1 ${viewMode === 'collabora' ? 'bg-[#185ABD] text-white shadow-sm' : 'text-[#185ABD] hover:text-[#134999] hover:bg-[#185ABD]/10'}`}>
                                    <Cloud size={12} /> Live Preview
                                </button>
                            </>
                        )}
                    </div>

                    {/* Template Mode Toggle */}
                    <button
                        onClick={() => setIsTemplateMode(!isTemplateMode)}
                        className={`mr-2 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all border ${isTemplateMode ? 'bg-indigo-50 text-indigo-600 border-indigo-200' : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                        title="Toggle Template Mode (Hide Content)"
                    >
                        {isTemplateMode ? <EyeOff size={14} /> : <Eye size={14} />}
                        Template Mode
                    </button>

                    {viewMode === 'paged' && (
                        <div className="flex items-center flex-1 justify-center gap-1 overflow-x-auto no-scrollbar" onMouseDown={preventFocusLoss}><div className="flex gap-0.5 border-r border-slate-200 pr-2 mr-2"><button onClick={handleUndo} className="p-1.5 rounded hover:bg-white text-slate-600"><RotateCcw size={16} /></button><button onClick={handleRedo} className="p-1.5 rounded hover:bg-white text-slate-600"><RotateCw size={16} /></button></div><div className="flex gap-0.5 border-r border-slate-200 pr-2 mr-2"><select onChange={(e) => execCmd('fontSize', e.target.value)} defaultValue="3" className="h-7 w-12 bg-slate-50 border border-slate-200 rounded text-xs px-1 outline-none font-bold text-slate-700"><option value="1">XS</option><option value="2">S</option><option value="3">M</option><option value="4">L</option><option value="5">XL</option><option value="6">2XL</option><option value="7">3XL</option></select><button onClick={() => execCmd('bold')} className="p-1.5 rounded hover:bg-slate-100 text-slate-700 font-bold"><Bold size={14} /></button><button onClick={() => execCmd('italic')} className="p-1.5 rounded hover:bg-slate-100 text-slate-700 italic"><Italic size={14} /></button><button onClick={() => execCmd('underline')} className="p-1.5 rounded hover:bg-slate-100 text-slate-700 underline"><Underline size={14} /></button></div><div className="flex gap-0.5 border-r border-slate-200 pr-2 mr-2"><button onClick={() => execCmd('justifyLeft')} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><AlignLeft size={14} /></button><button onClick={() => execCmd('justifyCenter')} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><AlignCenter size={14} /></button><button onClick={() => execCmd('justifyRight')} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><AlignRight size={14} /></button></div><div className="flex gap-0.5"><button onClick={() => execCmd('insertUnorderedList')} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><List size={14} /></button><button onClick={() => execCmd('insertOrderedList')} className="p-1.5 rounded hover:bg-slate-100 text-slate-600"><ListOrdered size={14} /></button></div></div>
                    )}
                    <div className="flex gap-2 items-center" onMouseDown={preventFocusLoss}><button onClick={() => setShowTemplates(true)} className="flex items-center gap-1.5 p-1.5 rounded hover:bg-white text-slate-600 bg-slate-100 text-xs font-bold px-2"><LayoutTemplate size={14} /> Templates</button>{mode === 'design' && (<button onClick={handleSaveAsNewTemplate} className="flex items-center gap-1.5 bg-white text-slate-600 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 transition-all border border-slate-200"><Copy size={12} /> Save Copy</button>)}<button onClick={repaginate} className="flex items-center gap-1.5 bg-indigo-50 text-indigo-600 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all border border-indigo-100"><Sparkles size={12} /> Auto Layout</button><div className="flex bg-slate-900 rounded-lg p-0.5 gap-0.5"><button onClick={() => handleCloudConvert('docx', false)} className="flex items-center gap-1.5 bg-slate-900 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all" title="Download Word (.docx)"><FilePlus size={12} /> .docx</button><div className="w-px bg-slate-700 my-1"></div><button onClick={() => handleCloudConvert('pdf', false)} className="flex items-center gap-1.5 bg-slate-900 text-white px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-black transition-all" title="Download PDF"><Download size={12} /> .pdf</button></div></div>
                </div>

                <div className="flex flex-1 overflow-hidden cv-page-container">
                    {(viewMode === 'continuous' || viewMode === 'export' || viewMode === 'collabora') && (
                        <CVContentSidebar
                            data={resumeData}
                            onChange={(newData) => {
                                recordHistory();
                                setResumeData(newData);
                                setPages(prev => mergeDataIntoStructure(prev, newData));
                            }}
                            vacancy={vacancies.find(v => v.id === selectedVacancyId)}
                            vacancies={vacancies}
                            selectedVacancyId={selectedVacancyId}
                            onSelectVacancy={setSelectedVacancyId}
                            candidates={candidates}
                            selectedCandidateId={selectedCandidateId}
                            onSelectCandidate={setSelectedCandidateId}
                            onTailor={handleGenerateTailoredCV}
                            isTailoring={isAiProcessing}
                            tailorResult={tailorResult}
                            onApplyTailoredChange={applyTailoredChange}
                            onApplyTailoredWorkItem={applyTailoredWorkItem}
                            clientId={user?.role === 'ADMIN' ? undefined : (user?.clientId || user?.id)}
                        />
                    )}

                    {viewMode === 'collabora' && (
                        <div className="flex-1 border-l border-slate-200 relative bg-slate-100 flex flex-col z-50">
                            <CollaboraLivePreview
                                resumeData={resumeData}
                                templateId={selectedProTemplateId}
                                pages={pages}
                                templateName={templateName}
                                user={user}
                                onSelectTemplate={(id) => setSelectedProTemplateId(id)}
                                availableTemplates={availableTemplates.filter(t => (t as any).file_type === 'docx' || ((t as any).title && !t.structure))}
                            />
                        </div>
                    )}

                    <div
                        ref={containerRef}
                        className={`flex-1 overflow-auto flex justify-center p-10 pt-16 custom-scrollbar relative cv-page-container ${viewMode === 'collabora' ? 'hidden' : ''}`}
                        onClick={() => { setSelectedDecoId(null); setSelectedSection(null); }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleCanvasDrop}
                    >
                        <div className="flex flex-col gap-8 cv-print-area cv-pages-wrapper" style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'top center' }}>
                            {pages.map((page, pageIndex) => (
                                <div
                                    key={pageIndex}
                                    id={`cv-page-${pageIndex}`}
                                    className="bg-white shadow-2xl relative cv-page transition-all duration-300"
                                    style={{
                                        width: `${A4_WIDTH_PX}px`,
                                        height: `${A4_HEIGHT_PX}px`,
                                        backgroundColor: design.backgroundColor,
                                        fontFamily: design.fontFamily,
                                        color: design.textColor,
                                        marginBottom: '32px'
                                    }}
                                >
                                    {/* Decorations Layer */}
                                    {decorations.filter(d => d.pageIndex === pageIndex || (d.allPages && viewMode !== 'continuous')).map(deco => (
                                        <div
                                            key={deco.id}
                                            className={`absolute group cursor-move ${selectedDecoId === deco.id ? 'ring-2 ring-red-0 no-print-ring' : ''}`}
                                            style={{
                                                left: deco.x, top: deco.y, width: deco.width, height: deco.height,
                                                zIndex: deco.zIndex ?? 10, opacity: deco.opacity ?? 1,
                                                transform: `rotate(${deco.rotation}deg)`,
                                                backgroundColor: deco.type === 'image' ? 'transparent' : (deco.color || design.primaryColor),
                                                borderRadius: deco.type === 'circle' ? '50%' : '0'
                                            }}
                                            onMouseDown={(e) => {
                                                e.stopPropagation();
                                                setSelectedDecoId(deco.id);
                                                setDragState({ isDragging: true, isResizing: false, type: 'decoration', targetId: deco.id, startX: e.clientX, startY: e.clientY, initialX: deco.x, initialY: deco.y, initialW: deco.width, initialH: deco.height });
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedDecoId(deco.id);
                                            }}
                                        >
                                            {deco.type === 'image' && deco.imageUrl && <img src={deco.imageUrl} className="w-full h-full object-cover pointer-events-none" />}
                                            {deco.type === 'text' && (
                                                <EditableText
                                                    value={deco.text}
                                                    onChange={(val: string) => {
                                                        setDecorations(prev => prev.map(d => d.id === deco.id ? { ...d, text: val } : d));
                                                    }}
                                                    isRich={true}
                                                    className="w-full h-full p-2"
                                                />
                                            )}

                                            {selectedDecoId === deco.id && (
                                                <div className="no-print">
                                                    {/* Resize Handles */}
                                                    <div className="absolute -right-1 -bottom-1 w-3 h-3 bg-white border border-red-0 rounded-full cursor-nwse-resize z-50"
                                                        onMouseDown={(e) => {
                                                            e.stopPropagation();
                                                            setDragState({ isDragging: false, isResizing: true, handle: 'se', type: 'decoration', targetId: deco.id, startX: e.clientX, startY: e.clientY, initialX: deco.x, initialY: deco.y, initialW: deco.width, initialH: deco.height });
                                                        }}
                                                    />
                                                    <div
                                                        className="absolute -top-3 -right-3 bg-red-500 text-white rounded-full p-1 cursor-pointer hover:bg-red-600 shadow-sm z-50"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            recordHistory();
                                                            setDecorations(prev => prev.filter(d => d.id !== deco.id));
                                                            setSelectedDecoId(null);
                                                        }}
                                                        title="Delete Element"
                                                    >
                                                        <X size={10} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}

                                    {/* Content Layer */}
                                    <div className={`relative z-10 transition-opacity duration-300 ${isTemplateMode ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} style={{
                                        paddingTop: `${design.paddingTop ?? design.pagePadding}px`,
                                        paddingBottom: `${design.paddingBottom ?? design.pagePadding}px`,
                                        paddingLeft: `${design.paddingLeft ?? design.pagePadding}px`,
                                        paddingRight: `${design.paddingRight ?? design.pagePadding}px`,
                                    }}>
                                        {page.map((section, sectionIndex) => (
                                            <div
                                                key={section.id}
                                                id={`section-${section.id}`}
                                                className={`mb-4 relative border hover:border-dashed hover:border-slate-300 transition-colors p-1 group/section cv-section-wrapper ${selectedSection?.sectionId === section.id ? 'border-red-0 border-dashed bg-red-/20 no-print-bg' : 'border-transparent'}`}
                                                onClick={(e) => { e.stopPropagation(); setSelectedSection({ pageIndex, sectionId: section.id }); setSelectedDecoId(null); }}
                                                draggable={section.styles?.position !== 'absolute'}
                                                onDragStart={(e) => section.styles?.position !== 'absolute' && handleSectionDragStart(e, pageIndex, sectionIndex)}
                                                onDrop={(e) => section.styles?.position !== 'absolute' && handleSectionDrop(e, pageIndex, sectionIndex)}
                                                onDragOver={(e) => e.preventDefault()}
                                                style={{ width: `${section.width || 100}%`, marginBottom: `${design.itemSpacing}px`, ...section.styles }}
                                                onMouseDown={(e) => {
                                                    if (section.styles?.position === 'absolute') {
                                                        e.stopPropagation();
                                                        setSelectedSection({ pageIndex, sectionId: section.id });
                                                        setDragState({
                                                            isDragging: true,
                                                            isResizing: false,
                                                            type: 'section',
                                                            targetId: section.id,
                                                            pageIndex: pageIndex,
                                                            startX: e.clientX,
                                                            startY: e.clientY,
                                                            initialX: parseFloat((section.styles?.left as string) || '0'),
                                                            initialY: parseFloat((section.styles?.top as string) || '0'),
                                                            initialW: 0,
                                                            initialH: 0
                                                        });
                                                    }
                                                }}
                                            >
                                                {section.type === 'header' && (
                                                    <div className="flex flex-col gap-1">
                                                        <EditableText tagName="h1" value={section.content?.name} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.content.name = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeHeading * 1.5}px`, color: design.primaryColor, fontWeight: 'bold', lineHeight: 1 }} />
                                                        <EditableText tagName="p" value={section.content?.role} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.content.role = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeBody * 1.2}px`, opacity: 0.8 }} />
                                                        <div style={{ fontSize: `${design.fontSizeBody}px`, opacity: 0.7, marginTop: 4, display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{(section.settings?.showEmail ?? true) && <span>{section.content?.email}</span>}{(section.settings?.showEmail ?? true) && (section.settings?.showPhone ?? true) && <span>â€¢</span>}{(section.settings?.showPhone ?? true) && <span>{section.content?.phone}</span>}</div>
                                                    </div>
                                                )}
                                                {section.type === 'summary' && (<div>{!section.settings?.hideTitle && <EditableText tagName="h3" value={section.title || 'Profielschets'} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.title = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeHeading}px`, color: design.primaryColor, marginBottom: '8px' }} />}<EditableText tagName="div" isRich={true} value={section.content?.text} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.content.text = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeBody}px`, display: 'block', width: '100%' }} placeholder="Profiel tekst..." /></div>)}
                                                {section.type === 'experience' && (
                                                    <div>
                                                        {!section.settings?.hideTitle && (
                                                            <EditableText tagName="h3" value={section.title || 'Werkervaring'} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.title = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeHeading}px`, color: design.primaryColor, marginBottom: '8px' }} />
                                                        )}
                                                        {(section.content?.items || []).map((item: any, i: number) => {
                                                            const isModern = design.layoutVariant === 'modern';
                                                            const isCompact = design.layoutVariant === 'compact';

                                                            return (
                                                                <div key={i} className={`mb-4 cv-section-item ${isCompact ? 'mb-2' : ''}`}>
                                                                    {/* MODERN: Date Top */}
                                                                    {isModern && (
                                                                        <div style={{ fontSize: `${design.fontSizeBody}px`, opacity: 0.7, marginBottom: 2 }}>{item.period}</div>
                                                                    )}

                                                                    <table style={{ width: '100%', borderCollapse: 'collapse', margin: 0, border: 'none' }} cellPadding={0} cellSpacing={0}>
                                                                        <tbody>
                                                                            <tr>
                                                                                <td style={{ verticalAlign: 'top', padding: 0, margin: 0 }}>
                                                                                    <div className="font-bold" style={{ fontSize: `${design.fontSizeBody + 1}px` }}>
                                                                                        {item.role}
                                                                                    </div>
                                                                                </td>
                                                                                {!isModern && (
                                                                                    <td style={{ verticalAlign: 'top', padding: 0, margin: 0, width: 'auto', textAlign: 'right' }}>
                                                                                        <div className="font-bold whitespace-nowrap" style={{ fontSize: `${design.fontSizeBody + 1}px`, opacity: 0.7 }}>
                                                                                            {item.period}
                                                                                        </div>
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>

                                                                    <div style={{ fontSize: `${design.fontSizeBody}px`, fontStyle: 'italic', marginBottom: 2, color: design.primaryColor, opacity: 0.9 }}>{item.company}</div>

                                                                    <EditableText tagName="div" isRich={true} value={item.description} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec && sec.content.items[i]) sec.content.items[i].description = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeBody}px`, whiteSpace: 'pre-wrap', display: 'block', width: '100%' }} />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {section.type === 'education' && (
                                                    <div>
                                                        {!section.settings?.hideTitle && (
                                                            <EditableText tagName="h3" value={section.title || 'Opleiding'} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.title = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeHeading}px`, color: design.primaryColor, marginBottom: '8px' }} />
                                                        )}
                                                        {(section.content?.items || []).map((item: any, i: number) => {
                                                            const isModern = design.layoutVariant === 'modern';
                                                            return (
                                                                <div key={i} className="mb-2 cv-section-item">
                                                                    {isModern && (
                                                                        <div style={{ fontSize: `${design.fontSizeBody}px`, opacity: 0.7, marginBottom: 1 }}>{item.year}</div>
                                                                    )}
                                                                    <table style={{ width: '100%', borderCollapse: 'collapse', margin: 0, border: 'none' }} cellPadding={0} cellSpacing={0}>
                                                                        <tbody>
                                                                            <tr>
                                                                                <td style={{ verticalAlign: 'top', padding: 0, margin: 0 }}>
                                                                                    <div className="font-bold" style={{ fontSize: `${design.fontSizeBody + 1}px` }}>
                                                                                        {item.degree}
                                                                                    </div>
                                                                                </td>
                                                                                {!isModern && (
                                                                                    <td style={{ verticalAlign: 'top', padding: 0, margin: 0, width: 'auto', textAlign: 'right' }}>
                                                                                        <div className="font-bold whitespace-nowrap" style={{ fontSize: `${design.fontSizeBody + 1}px`, opacity: 0.7 }}>
                                                                                            {item.year}
                                                                                        </div>
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                    <div style={{ fontSize: `${design.fontSizeBody}px` }}>{item.school}</div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {section.type === 'skills' && (<div>{!section.settings?.hideTitle && <EditableText tagName="h3" value={section.title || 'Vaardigheden'} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.title = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeHeading}px`, color: design.primaryColor, marginBottom: '8px' }} />}<div className="flex flex-wrap gap-2">{(section.content?.items || []).map((tag: any, i: number) => (<span key={i} style={{ fontSize: `${design.fontSizeBody}px`, padding: '4px 8px', background: design.tagColor || '#f1f5f9', color: design.tagTextColor || '#334155', borderRadius: '4px' }}>{typeof tag === 'string' ? tag : tag.name || tag}</span>))}</div></div>)}
                                                {section.type === 'custom_text' && (
                                                    <div className="w-full block">
                                                        {(section.content?.items && section.content.items.length > 0) ? (
                                                            section.content.items.map((item: any, i: number) => (
                                                                <div key={i} className={`cv-section-item w-full block ${i < section.content.items.length - 1 ? 'mb-4' : ''}`}>
                                                                    {(!section.settings?.hideTitle && item.name) && (
                                                                        <EditableText tagName="h3" value={item.name} onChange={(val: string) => { /* Sync handled by sidebar */ }} style={{ fontSize: `${design.fontSizeHeading}px`, color: design.primaryColor, marginBottom: '8px', display: 'block', width: '100%' }} />
                                                                    )}
                                                                    {(!section.settings?.hideTitle && !item.name && section.title) && (
                                                                        <EditableText tagName="h3" value={section.title} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.title = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeHeading}px`, color: design.primaryColor, marginBottom: '8px', display: 'block', width: '100%' }} />
                                                                    )}
                                                                    {isPreviewMode ? (
                                                                        <div className="whitespace-pre-wrap block w-full" style={{ fontSize: `${design.fontSizeBody}px` }} dangerouslySetInnerHTML={{ __html: processTemplate(item.value || item.text || '', resumeData).replace(/\n/g, '<br/>') }} />
                                                                    ) : (
                                                                        <div className="block w-full">
                                                                            <EditableText tagName="div" isRich={true} value={item.value || item.text} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec && sec.content.items[i]) { sec.content.items[i].value = val; sec.content.items[i].text = val; } setPages(newPages); }} style={{ fontSize: `${design.fontSizeBody}px`, display: 'block', width: '100%' }} placeholder="Vul vrije tekst in..." />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ))
                                                        ) : (
                                                            <div className="w-full block">
                                                                {!section.settings?.hideTitle && (
                                                                    <EditableText tagName="h3" value={section.title || 'Sectie'} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.title = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeHeading}px`, color: design.primaryColor, marginBottom: '8px', display: 'block', width: '100%' }} />
                                                                )}
                                                                {isPreviewMode ? (
                                                                    <div
                                                                        className="whitespace-pre-wrap block w-full"
                                                                        style={{ fontSize: `${design.fontSizeBody}px` }}
                                                                        dangerouslySetInnerHTML={{
                                                                            __html: processTemplate(section.content?.text || '', resumeData).replace(/\n/g, '<br/>')
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <div className="block w-full">
                                                                        <EditableText tagName="div" isRich={true} value={section.content?.text} onChange={(val: string) => { const newPages = [...pages]; const sec = newPages[pageIndex].find(s => s.id === section.id); if (sec) sec.content.text = val; setPages(newPages); }} style={{ fontSize: `${design.fontSizeBody}px`, display: 'block', width: '100%' }} placeholder="Vul vrije tekst in..." />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* EXPORT TO WORD VIEW - NOW A TRANSLUCENT OVERLAY INSIDE THE RIGHT PANEL */}
                        {viewMode === 'export' && (
                            <div className="absolute right-8 top-8 bottom-8 w-[380px] z-[100] flex flex-col items-end pointer-events-none animate-slideLeft">
                                {selectedProTemplateId ? (
                                    <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-slate-200/60 w-full flex flex-col relative overflow-hidden animate-scaleIn pointer-events-auto">
                                        {/* Action Card Header */}
                                        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                                            <div>
                                                <h3 className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                                                    <FileText size={14} className="text-indigo-600"/> Kandidaat CV Optimaliseren
                                                </h3>
                                                <p className="text-[10px] text-slate-500 mt-0.5 font-medium">Slimme Word Document Generator.</p>
                                            </div>
                                            <button onClick={() => setSelectedProTemplateId(null)} className="p-1.5 rounded-md hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors">
                                                <X size={14}/>
                                            </button>
                                        </div>
                                        
                                        <div className="p-5 overflow-y-auto">
                                            <div className="bg-indigo-50/50 border border-indigo-100 rounded-xl p-4 mb-5">
                                                <h4 className="text-[10px] font-black uppercase tracking-widest text-indigo-900 flex items-center gap-2 mb-2">
                                                    <Wand2 size={12} className="text-indigo-600"/> Hoe werkt dit?
                                                </h4>
                                                <div className="text-[11px] text-indigo-900/80 space-y-2 leading-relaxed font-medium">
                                                    <p>Bewerk de gegevens links in de <strong className="text-indigo-900">Inhoud Editor</strong>. Alle velden worden naadloos overgenomen in het Pro Template, inclusief complexe lijsten zoals werkervaring en opleidingen.</p>
                                                    <p>Gebruik de knop '<strong className="text-indigo-900">Optimaliseer</strong>' bovenaan de editor om Froukje de <span className="italic">Functietitel</span> en <span className="italic">Profielschets</span> automatisch te laten herschrijven op basis van een vacature.</p>
                                                </div>
                                            </div>
                                            
                                            <div className="border border-slate-200 rounded-xl p-4 bg-white">
                                                <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-900 flex items-center gap-2 mb-3">
                                                    <Code size={12} className="text-slate-500"/> Bekende shortcodes:
                                                </h4>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {['{name}', '{role}', '{phone}', '{email}', '{location}', '{summary}', '{#workHistory}', '{#education}'].map(code => (
                                                        <span key={code} className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-[9px] font-mono text-slate-600 font-bold">{code}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="p-4 border-t border-slate-100 bg-slate-50 flex flex-col gap-2 mt-auto">
                                            <button 
                                                onClick={() => handleGenerateProWord()}
                                                disabled={isGeneratingWord}
                                                className={`w-full py-2.5 text-[11px] font-bold text-white rounded-lg flex items-center justify-center gap-2 shadow-sm transition-all ${isGeneratingWord ? 'bg-indigo-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 hover:-translate-y-0.5'}`}
                                            >
                                                {isGeneratingWord ? (
                                                    <><Loader2 size={14} className="animate-spin" /> Document Genereren...</>
                                                ) : (
                                                    <><FileText size={14} /> Openen in Word</>
                                                )}
                                            </button>
                                            <button onClick={() => setViewMode('continuous')} className="w-full py-2 text-[11px] font-bold text-slate-500 hover:bg-slate-200 rounded-lg transition-colors">
                                                Annuleren
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-slate-200/50 w-full h-full flex flex-col overflow-hidden animate-scaleIn pointer-events-auto">
                                        <div className="flex items-center justify-between p-5 border-b border-slate-100 shrink-0 bg-slate-50/50">
                                            <div>
                                                <h2 className="text-sm font-black text-slate-900 tracking-tight uppercase">Word Export</h2>
                                                <p className="text-[10px] font-medium text-slate-500 mt-0.5">Selecteer een sjabloon.</p>
                                            </div>
                                            <button onClick={() => setViewMode('continuous')} className="p-1.5 bg-white text-slate-400 border border-slate-200 hover:bg-slate-100 hover:text-slate-900 rounded-lg transition-all shadow-sm">
                                                <X size={14} />
                                            </button>
                                        </div>

                                        <div className="px-5 pt-3 border-b border-slate-100 flex flex-col gap-3 shrink-0 bg-white">
                                            <div className="flex gap-4 relative">
                                                <button 
                                                    onClick={() => setExportTab('templates')}
                                                    className={`pb-3 text-[10px] font-black tracking-widest uppercase transition-colors relative ${exportTab === 'templates' ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                                                >
                                                    Pro Templates
                                                    {exportTab === 'templates' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full"></div>}
                                                </button>
                                                <button 
                                                    onClick={() => setExportTab('shortcodes')}
                                                    className={`pb-3 text-[10px] font-black tracking-widest uppercase transition-colors relative flex items-center gap-1.5 ${exportTab === 'shortcodes' ? 'text-emerald-600' : 'text-slate-400 hover:text-slate-600'}`}
                                                >
                                                    Shortcodes
                                                    {exportTab === 'shortcodes' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-emerald-600 rounded-t-full"></div>}
                                                </button>
                                            </div>
                                            
                                            {exportTab === 'templates' && (
                                                <div className="pb-3 relative w-full">
                                                    <Search size={12} className="absolute left-3 top-2.5 text-slate-400" />
                                                    <input 
                                                        type="text" 
                                                        placeholder="Zoek sjablonen..." 
                                                        value={exportSearchTerm}
                                                        onChange={(e) => setExportSearchTerm(e.target.value)}
                                                        className="w-full text-[11px] font-bold pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-indigo-500 focus:bg-white focus:ring-1 focus:ring-indigo-500 transition-colors"
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
                                            {exportTab === 'templates' ? (
                                                <div className="grid grid-cols-2 gap-3">
                                                    {availableTemplates
                                                        .filter(t => t.name.toLowerCase().includes(exportSearchTerm.toLowerCase()))
                                                        .map(template => (
                                                        <div 
                                                            key={template.id} 
                                                            className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-indigo-400 hover:shadow-lg transition-all cursor-pointer group flex flex-col hover:-translate-y-0.5"
                                                            onClick={() => setSelectedProTemplateId(template.id)}
                                                        >
                                                            <div className="aspect-[1/1.414] bg-slate-100 relative overflow-hidden flex items-center justify-center border-b border-slate-100">
                                                                {template.thumbnailUrl ? (
                                                                    <img src={template.thumbnailUrl} alt={template.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                                                ) : (
                                                                    <div className="text-slate-400 flex flex-col items-center gap-2">
                                                                        <FileText size={24} className="opacity-40" />
                                                                        <span className="text-[9px] font-bold tracking-widest uppercase opacity-40">.DOCX</span>
                                                                    </div>
                                                                )}
                                                                <div className="absolute inset-0 bg-indigo-900/0 group-hover:bg-indigo-900/5 transition-colors"></div>
                                                            </div>
                                                            <div className="p-3 flex flex-col flex-1">
                                                                <h3 className="font-bold text-slate-800 text-xs truncate leading-snug">{template.name}</h3>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    
                                                    {availableTemplates.filter(t => t.name.toLowerCase().includes(exportSearchTerm.toLowerCase())).length === 0 && (
                                                        <div className="col-span-full py-10 flex flex-col items-center justify-center text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">
                                                            <Search size={24} className="mb-3 text-slate-300" />
                                                            <p className="font-bold text-xs tracking-widest uppercase">Geen sjablonen.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-3 mb-4 mt-2">
                                                        <div className="p-2.5 bg-emerald-100/50 border border-emerald-200 text-emerald-600 rounded-xl shrink-0"><Code size={20}/></div>
                                                        <div>
                                                            <h2 className="text-sm font-black text-slate-900 uppercase tracking-tight">Shortcodes Guide</h2>
                                                            <p className="text-[10px] font-medium text-slate-500 mt-0.5 leading-snug">Typ deze in je Word document om data te koppelen.</p>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-4">
                                                        {/* Persoonsgegevens */}
                                                        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                                            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/80">
                                                                <h3 className="text-[10px] font-black uppercase tracking-widest text-blue-600">Persoonsgegevens</h3>
                                                            </div>
                                                            <div className="p-4 grid gap-2.5">
                                                                {[
                                                                    { label: 'Naam', code: '{name}' },
                                                                    { label: 'E-mail', code: '{email}' },
                                                                    { label: 'Telefoon', code: '{phone}' },
                                                                    { label: 'Functietitel', code: '{role}' },
                                                                    { label: 'Locatie', code: '{location}' },
                                                                    { label: 'LinkedIn', code: '{linkedin}' },
                                                                    { label: 'Introductie', code: '{summary}' }
                                                                ].map((item, i) => (
                                                                    <div key={i} className="flex items-center justify-between p-2.5 border border-slate-100 rounded-lg hover:border-slate-300 transition-colors bg-white">
                                                                        <span className="text-xs font-bold text-slate-700">{item.label}</span>
                                                                        <code className="text-[10px] font-mono font-bold px-2 py-1 bg-slate-50 text-slate-600 border border-slate-200 rounded shrink-0">{item.code}</code>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>

                                                        {/* Loops */}
                                                        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                                            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/80">
                                                                <h3 className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Lijsten (Loops)</h3>
                                                            </div>
                                                            <div className="p-4">
                                                                <p className="text-[10px] text-slate-500 font-medium mb-4 leading-snug">Gebruik de <code className="text-emerald-600 font-bold px-1 bg-emerald-50 rounded">#</code> en <code className="text-emerald-600 font-bold px-1 bg-emerald-50 rounded">/</code> tags om een sectie te herhalen.</p>
                                                                
                                                                <div className="flex flex-col gap-4">
                                                                    <div className="border border-slate-100 bg-slate-50/50 rounded-lg p-3">
                                                                        <h4 className="text-[11px] font-bold text-slate-800 flex items-center gap-1.5 mb-2.5"><Briefcase size={12} className="text-slate-400"/> Werkervaring</h4>
                                                                        <pre className="text-[10px] font-mono leading-loose bg-slate-800 text-slate-300 p-3 rounded-md overflow-x-auto selection:bg-indigo-500/30">
{`{#workHistory}
Functie: {role}
Bedrijf: {company}
Periode: {period}
{description}
{/workHistory}`}
                                                                        </pre>
                                                                    </div>
                                                                    <div className="border border-slate-100 bg-slate-50/50 rounded-lg p-3">
                                                                        <h4 className="text-[11px] font-bold text-slate-800 flex items-center gap-1.5 mb-2.5"><GraduationCap size={12} className="text-slate-400"/> Opleidingen</h4>
                                                                        <pre className="text-[10px] font-mono leading-loose bg-slate-800 text-slate-300 p-3 rounded-md overflow-x-auto selection:bg-indigo-500/30">
{`{#education}
Studie: {degree}
School: {school}
Jaar: {year}
{description}
{/education}`}
                                                                        </pre>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>


                    {/* AI TAILORING RIGHT SIDEBAR */}

                </div>                            {showSaveVersionModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 no-print">
                        <div className="bg-white p-6 rounded-xl shadow-xl w-[500px] animate-scaleIn max-h-[80vh] flex flex-col">
                            <h3 className="text-lg font-bold mb-4">Versie Opslaan</h3>

                            <div className="mb-4">
                                <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Naam van deze versie</label>
                                <input
                                    autoFocus
                                    className="w-full p-3 border border-slate-300 rounded-lg outline-none focus:border-red-0 font-medium"
                                    placeholder="Bijv. Versie Google, Versie Sales..."
                                    value={savedVersionName}
                                    onChange={e => setSavedVersionName(e.target.value)}
                                />
                            </div>

                            <div className="flex-1 overflow-y-auto mb-6 border border-slate-200 rounded-lg bg-slate-50 p-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase block mb-2 px-2">Bestaande Versies</label>
                                <ExistingVersionsList candidateId={initialData?.candidateId || initialData?.id || ''} onSelect={(name) => setSavedVersionName(name)} />
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                                <button onClick={() => setShowSaveVersionModal(false)} className="px-4 py-2 text-slate-500 hover:text-slate-900 font-medium text-sm">Annuleren</button>

                                {/* Save As New Button */}
                                <button
                                    onClick={() => handleSaveVersion(savedVersionName, true)}
                                    disabled={savingTemplate || !savedVersionName}
                                    className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-50 text-sm font-bold flex items-center gap-2"
                                >
                                    <FilePlus size={16} /> Opslaan als Nieuw
                                </button>

                                {/* Overwrite / Save Button */}
                                <button
                                    onClick={() => handleSaveVersion(savedVersionName, false)}
                                    disabled={savingTemplate || !savedVersionName}
                                    className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-black disabled:opacity-50 text-sm font-bold flex items-center gap-2"
                                >
                                    <Save size={16} /> Opslaan
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Overwrite Warning Modal */}
                {showOverwriteWarning && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-slate-900/50 backdrop-blur-sm animate-fadeIn no-print">
                        <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden flex flex-col p-8 text-center animate-scaleIn">
                            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 mx-auto mb-4">
                                <AlertCircle size={32} />
                            </div>
                            <h3 className="text-xl font-black uppercase tracking-tight text-slate-900 mb-2">Waarschuwing</h3>
                            <p className="text-sm font-medium text-slate-500 mb-8">
                                Er bestaat al een versie met de naam <strong className="text-slate-800">{pendingSaveName}</strong>. Wil je deze versie overschrijven?
                            </p>
                            <div className="flex gap-4">
                                <button onClick={() => setShowOverwriteWarning(false)} className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold uppercase tracking-widest rounded-xl hover:bg-slate-200 transition-all text-xs">
                                    Annuleren
                                </button>
                                <button onClick={() => handleSaveVersion(pendingSaveName, true)} className="flex-1 py-3 bg-orange-600 text-white font-bold uppercase tracking-widest rounded-xl hover:bg-orange-700 shadow-lg shadow-orange-100 transition-all text-xs">
                                    Overschrijven
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <ContentEditorModal
                    isOpen={showContentEditor}
                    onClose={() => setShowContentEditor(false)}
                    data={resumeData}
                    onChange={(newData) => {
                        recordHistory();
                        setResumeData(newData);
                        // Also trigger a re-merge into pages if needed?
                        // The 'pages' state is derived from resumeData usually via 'mergeDataIntoStructure' or manual content.
                        // Actually, 'pages' is distinct from 'resumeData' in this builder. 'resumeData' is the source of truth for the 'data' but 'pages' is the visual representation.
                        // We need to update 'pages' too.
                        // Let's call a helper to update pages content from new resumeData.
                        // For now, simpler: user edits resumeData, we have to update visually.
                        // We see 'applyTailoredChange' does manual update of pages.
                        // We should probably trigger a 'refreshPagesFromData' or similar.
                        // For now, updating resumeData is step 1.
                        // To make it visible, we need to iterate pages and update content.
                        // Simple hack: Update all sections that match the type.

                        const newPages = [...pages];
                        // Update Summary
                        if (newData.summary) {
                            for (let p = 0; p < newPages.length; p++) {
                                const s = newPages[p].find(sec => sec.type === 'summary');
                                if (s) s.content.text = newData.summary;
                            }
                        }
                        // Update Experience
                        // This is harder because items might change count.
                        // A full re-render from structure might be better, but we lose design changes?
                        // No, structure is separate from design (colors etc).
                        // But if we dragged sections...
                        // Let's just update resumeData for now and assume the user might need to re-apply the data to the layout if they want.
                        // Wait, if I edit text in the modal, I expect it to change on the page.
                        // I will attempt to update the 'experience' sections content if found.
                        for (let p = 0; p < newPages.length; p++) {
                            const s = newPages[p].find(sec => sec.type === 'experience');
                            if (s) {
                                s.content.items = newData.workHistory.map(w => ({ role: w.role, company: w.company, period: w.period, description: w.description }));
                            }
                            const edu = newPages[p].find(sec => sec.type === 'education');
                            if (edu) {
                                edu.content.items = newData.education.map(e => ({ school: e.school, degree: e.degree, year: e.year }));
                            }
                        }
                        setPages(newPages);
                    }}
                />
                
                {showTemplates && (
                    <TemplateSelectorModal 
                        isOpen={showTemplates} 
                        onClose={() => setShowTemplates(false)} 
                        templates={availableTemplates}
                        currentData={resumeData}
                        onSelect={(tmpl) => {
                            handleLoadTemplate(tmpl);
                            setShowTemplates(false);
                        }}
                        mergeDataIntoStructure={mergeDataIntoStructure}
                        processTemplate={processTemplate}
                    />
                )}
            </div >

        </div>
    );
};

const TemplateSelectorModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    templates: CVTemplate[];
    currentData: CVData;
    onSelect: (template: CVTemplate) => void;
    mergeDataIntoStructure: (structure: CVSection[][], data: CVData) => CVSection[][];
    processTemplate: (text: string, data: CVData) => string;
}> = ({ isOpen, onClose, templates, currentData, onSelect, mergeDataIntoStructure, processTemplate }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[200] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-fadeIn">
            <div className="bg-slate-50 w-full max-w-7xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scaleIn">
                <div className="p-6 bg-white border-b border-slate-200 flex justify-between items-center z-10 shrink-0">
                    <div>
                        <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                            <LayoutTemplate className="text-[#e51b23]" /> Kies een Template
                        </h2>
                        <p className="text-xs text-slate-500 font-medium mt-1">
                            Bekijk hoe jouw gegevens eruitzien in verschillende ontwerpen.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
                        <X size={24} />
                    </button>
                </div>
                
                <div className="flex-1 overflow-y-auto p-6 md:p-8 custom-scrollbar">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {templates.map(tmpl => {
                            const structure = tmpl.structure ? JSON.parse(JSON.stringify(tmpl.structure)) : [];
                            const previewStructure = mergeDataIntoStructure(structure, currentData);
                            
                            const design = {
                                fontFamily: 'Inter, sans-serif',
                                primaryColor: '#000000',
                                textColor: '#000000',
                                backgroundColor: '#ffffff',
                                pagePadding: 40,
                                itemSpacing: 20,
                                fontSizeBody: 12,
                                fontSizeHeading: 24,
                                ...(tmpl.design || {})
                            };
                            
                            const page0 = previewStructure[0] || [];
                            
                            return (
                                <div 
                                    key={tmpl.id} 
                                    className="bg-white border hover:border-[#e51b23] border-slate-200 rounded-xl overflow-hidden group hover:shadow-xl transition-all relative flex flex-col cursor-pointer"
                                    onClick={() => onSelect(tmpl)}
                                >
                                    <div className="relative w-full aspect-[210/297] bg-slate-100 overflow-hidden border-b border-slate-100 flex items-center justify-center">
                                        <div className="absolute top-0 left-0 origin-top-left pointer-events-none" style={{ transform: 'scale(0.24)', width: '794px', height: '1123px' }}>
                                            <div style={{
                                                width: '794px', 
                                                minHeight: '1123px',
                                                backgroundColor: design.backgroundColor,
                                                fontFamily: design.fontFamily,
                                                color: design.textColor,
                                                padding: `${design.pagePadding}px`,
                                                position: 'relative',
                                                boxSizing: 'border-box'
                                            }}>
                                                {page0.map((section: CVSection) => (
                                                    <div
                                                        key={section.id}
                                                        style={{
                                                            marginBottom: `${design.itemSpacing}px`,
                                                            position: (section.styles as any)?.position === 'absolute' ? 'absolute' : 'relative',
                                                            left: (section.styles as any)?.left,
                                                            top: (section.styles as any)?.top,
                                                            width: (section.styles as any)?.width || ((section.styles as any)?.position === 'absolute' ? 'auto' : '100%'),
                                                            ...section.styles
                                                        }}
                                                    >
                                                        {section.type === 'header' && (
                                                            <div>
                                                                <h1 style={{ color: design.primaryColor, fontSize: `${design.fontSizeHeading * 1.5}px`, fontWeight: 'bold', margin:0 }}>{section.content?.name || 'Naam'}</h1>
                                                                <p style={{ fontSize: `${design.fontSizeBody * 1.2}px`, opacity: 0.8, margin:0 }}>{section.content?.role || 'Functie'}</p>
                                                            </div>
                                                        )}
                                                        {(section.type === 'summary' || section.type === 'custom_text') && (
                                                            <div>
                                                                {section.title && !section.settings?.hideTitle && <h3 style={{ color: design.primaryColor, fontSize: design.fontSizeHeading, borderBottom: `2px solid ${design.primaryColor}`, marginBottom: 5, margin:0 }}>{section.title}</h3>}
                                                                <div style={{ fontSize: design.fontSizeBody, whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: processTemplate(section.content?.text || 'Tekst...', currentData).replace(/\n/g, '<br/>') }} />
                                                            </div>
                                                        )}
                                                        {section.type === 'experience' && (
                                                            <div>
                                                                {!section.settings?.hideTitle && <h3 style={{ color: design.primaryColor, fontSize: design.fontSizeHeading, borderBottom: `2px solid ${design.primaryColor}`, marginBottom: 5, margin:0 }}>{section.title || 'Werkervaring'}</h3>}
                                                                {(section.content?.items || []).slice(0, 3).map((item: any, i: number) => (
                                                                    <div key={i} style={{ marginBottom: 10 }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <span style={{ fontWeight: 'bold', fontSize: `${design.fontSizeBody + 1}px` }}>{item.role}</span>
                                                                            <span style={{ fontSize: `${design.fontSizeBody}px`, opacity: 0.8 }}>{item.period}</span>
                                                                        </div>
                                                                        <div style={{ fontSize: `${design.fontSizeBody}px`, opacity: 0.8, color: design.primaryColor }}>{item.company}</div>
                                                                        <div style={{ fontSize: `${design.fontSizeBody}px`, opacity: 0.7 }} dangerouslySetInnerHTML={{ __html: item.description ? item.description.substring(0, 100) + '...' : '' }} />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {section.type === 'education' && (
                                                            <div>
                                                                {!section.settings?.hideTitle && <h3 style={{ color: design.primaryColor, fontSize: design.fontSizeHeading, borderBottom: `2px solid ${design.primaryColor}`, marginBottom: 5, margin:0 }}>{section.title || 'Opleiding'}</h3>}
                                                                {(section.content?.items || []).slice(0, 2).map((item: any, i: number) => (
                                                                    <div key={i} style={{ marginBottom: 10 }}>
                                                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                                            <span style={{ fontWeight: 'bold', fontSize: `${design.fontSizeBody + 1}px` }}>{item.degree}</span>
                                                                            <span style={{ fontSize: `${design.fontSizeBody}px`, opacity: 0.8 }}>{item.year}</span>
                                                                        </div>
                                                                        <div style={{ fontSize: `${design.fontSizeBody}px`, opacity: 0.8 }}>{item.school}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {section.type === 'skills' && (
                                                            <div>
                                                                {!section.settings?.hideTitle && <h3 style={{ color: design.primaryColor, fontSize: design.fontSizeHeading, borderBottom: `2px solid ${design.primaryColor}`, marginBottom: 5, margin:0 }}>{section.title || 'Vaardigheden'}</h3>}
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                    {(section.content?.items || []).slice(0, 8).map((tag: any, i: number) => (
                                                                        <span key={i} style={{ fontSize: `${design.fontSizeBody}px`, padding: '2px 6px', background: design.tagColor || '#f1f5f9', color: design.tagTextColor || '#334155', borderRadius: '4px' }}>
                                                                            {typeof tag === 'string' ? tag : tag.name || tag}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-4 bg-white relative z-10 border-t border-slate-100 flex-1 flex flex-col justify-between">
                                        <h3 className="text-sm font-bold text-slate-900 truncate">{tmpl.name}</h3>
                                        <div className="mt-2 text-xs text-slate-400 font-medium">Klik om te kiezen</div>
                                    </div>
                                    <div className="absolute inset-0 bg-red-900/0 group-hover:bg-[#e51b23]/5 transition-colors z-20" />
                                </div>
                            )
                        })}
                        {templates.length === 0 && (
                            <div className="col-span-full py-12 flex flex-col items-center justify-center text-slate-400 bg-white rounded-xl border border-dashed border-slate-200">
                                <LayoutTemplate size={32} className="mb-4 text-slate-300" />
                                <p className="font-bold text-sm tracking-widest uppercase">Geen sjablonen gevonden.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const CollaboraLivePreview: React.FC<{
    resumeData: CVData;
    templateId: string | null;
    pages: CVSection[][];
    templateName: string;
    user: AppUser | null;
    onSelectTemplate: (id: string) => void;
    availableTemplates: any[];
}> = ({ resumeData, templateId, pages, templateName, user, onSelectTemplate, availableTemplates }) => {
    
    const [embedUrl, setEmbedUrl] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    
    const [debouncedData, setDebouncedData] = useState(resumeData);
    
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedData(resumeData);
        }, 2000);
        return () => clearTimeout(handler);
    }, [resumeData]);

    useEffect(() => {
        const generatePreview = async () => {
            if (!templateId) {
                setEmbedUrl(null);
                return;
            }
            
            setIsGenerating(true);
            try {
                const cid = debouncedData.candidateId || debouncedData.id || 'preview';
                
                const apiUrl = window.location.hostname === 'localhost' ? 'http://localhost:4000' : '';
                const response = await fetch(`${apiUrl}/api/documents/generate-cv`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        candidateId: cid,
                        templateId: templateId,
                        userId: user?.id,
                        title: `Live Preview - ${templateName}`,
                        pages: pages
                    })
                });

                if (!response.ok) {
                    throw new Error("Failed to generate preview");
                }

                const doc = await response.json();
                
                const backendUrl = (import.meta as any).env.VITE_WOPI_SERVER_URL || 'http://localhost:3000';
                const collaboraUrl = (import.meta as any).env.VITE_COLLABORA_URL || 'http://localhost:9980';
                
                const wopiSrc = encodeURIComponent(`${backendUrl}/wopi/files/${doc.id}`);
                const finalUrl = `${collaboraUrl}/browser/dist/cool.html?WOPISrc=${wopiSrc}&title=${encodeURIComponent('Live Preview')}`;
                
                setEmbedUrl(finalUrl);

            } catch (err) {
                console.error("Preview error", err);
            } finally {
                setIsGenerating(false);
            }
        };
        
        generatePreview();
    }, [debouncedData, templateId, pages, templateName, user?.id]);

    if (!templateId) {
        return (
            <div className="flex-1 bg-slate-50 flex flex-col items-center justify-center p-8 overflow-y-auto">
                <Cloud size={48} className="text-[#185ABD] mb-4 opacity-50" />
                <h2 className="text-xl font-black text-slate-800 mb-2">Word Live Preview</h2>
                <p className="text-sm text-slate-500 mb-8 max-w-sm text-center">
                    Selecteer een Pro Template om de data live en direct in een Microsoft Word document te zien.
                </p>
                <div className="grid grid-cols-2 gap-4 w-full max-w-xl">
                    {availableTemplates.map(t => (
                        <div key={t.id} onClick={() => onSelectTemplate(t.id)} className="bg-white border border-slate-200 p-4 rounded-xl cursor-pointer hover:border-[#185ABD] hover:shadow-md transition-all flex items-center gap-4 group">
                            <div className="w-12 h-16 bg-slate-100 border border-slate-200 rounded shrink-0 flex items-center justify-center text-[#185ABD] group-hover:bg-[#185ABD]/10 transition-colors">
                                <FileText size={20} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-bold text-slate-800 truncate">{t.name || t.title}</h3>
                                <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">Kies dit template</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 w-full h-full relative flex flex-col bg-slate-100">
            {isGenerating && (
                <div className="absolute top-4 right-4 bg-white shadow-lg rounded-full px-4 py-2 flex items-center gap-2 z-50 text-xs font-bold text-slate-700 animate-fadeIn">
                    <Loader2 size={14} className="animate-spin text-[#185ABD]" />
                    Document Updaten...
                </div>
            )}
            
            {embedUrl ? (
                <iframe 
                    src={embedUrl}
                    className="w-full h-full border-none bg-white"
                    title="Collabora Live Preview"
                />
            ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400">
                    <Loader2 size={48} className="animate-spin mb-4 text-[#185ABD] opacity-50" />
                    <p className="font-bold tracking-widest uppercase text-sm">Preview Voorbereiden...</p>
                </div>
            )}
        </div>
    );
};

export default CVBuilder;
